// MmtMetricsDashboard.jsx
//
// NEW "MMT Metrics" dashboard, built like the Booking Metrics dashboard: ONE table that adapts to
// the metrics ticked in the "Metrics" checklist (old and new MMT metrics together).
//
//   Performance (OLD MMT Dashboard data)            <- /api/mmt/summary   (unchanged endpoint)
//     Room Nights, Revenue, ASP, Property Visits, Conversion — per date: REVX (+ COMP / VAR buttons)
//     followed by totals (RN/Revenue/Visits summed, ASP = Revenue / RN, Conversion averaged)
//   Extranet (NEW, GO-MMT Extranet 1.1)             <- /api/mmt-metrics/data
//     Promotions   : Promotion Name, Tier, Max Discount, Room Night (+ Status)
//     Spotlight    : Enrollment Status, Eligibility Status, Visit Boost Pct, Performance Lookback
//     Content      : Total Score, Photos / Amenities / Properties / Policies Pending, Boost
//     Cancellation : Rules, Black Out Dates
//     Inclusions   : Description (+ Status, Rooms)
//     LMR          : Status, Start Time
//     shown as columns next to the hotel name; ticking a metric also adds a filter for each of its
//     columns (all filters combine). Each metric keeps its own filters.
//   Rating: MMT and Goibibo (Rankly), always shown as fixed columns   <- /api/rankly/summary
//
// Location and Agent are filters only (not columns), like Booking Metrics. A "Last sync" pill is
// shown for every data source on screen.
//
// Self-contained on purpose: it does not import from or change Dashboard.jsx's MMT Dashboard.
// Its styles are inside this file (MM_CSS, classes start with mm-); filters/buttons reuse the
// global rd-* classes from Dashboard.css so the page looks like the other dashboards.
import React, { useState, useMemo, useEffect, useLayoutEffect, useRef } from "react";
import { ChevronDown, X, Eraser, Search, RefreshCw, RotateCcw, CloudDownload } from "lucide-react";


// ---------------------------------------------------------------- styles (only for this page)
const MM_CSS = `
.mm-page { flex: 1; display: flex; flex-direction: column; min-height: 0; overflow: hidden; background: #fff; }
.mm-header { padding: 16px 24px 10px; background: #fff; }
.mm-title { font-size: 16px; font-weight: 700; color: #0f172a; letter-spacing: 0.02em; }
.mm-sync-line { display: flex; gap: 8px; flex-wrap: wrap; margin-top: 8px; align-items: center; }
/* ---------- "Last sync" status: one pill per data source, coloured by age.
   green = synced in the last 36 h, amber = older (a daily sync was missed), grey = never.
   A pill with details (e.g. Extranet) opens a small list with each metric's own time. */
.mm-sync-pill {
  position: relative; display: inline-flex; align-items: center; gap: 6px;
  font-size: 12px; font-weight: 600; border-radius: 999px; padding: 3px 10px; border: 1px solid;
  background: #f1f5f9; border-color: #e2e8f0; color: #475569; white-space: nowrap; line-height: 1.5;
}
.mm-sync-pill.mm-sync-fresh { background: #ecfdf5; border-color: #a7f3d0; color: #047857; }
.mm-sync-pill.mm-sync-stale { background: #fffbeb; border-color: #fcd34d; color: #b45309; }
.mm-sync-dot { width: 7px; height: 7px; border-radius: 50%; background: currentColor; flex-shrink: 0; }
.mm-sync-label { font-weight: 800; }
.mm-sync-ago { font-weight: 500; opacity: 0.85; }
button.mm-sync-pill { cursor: pointer; font-family: inherit; }
button.mm-sync-pill:hover { filter: brightness(0.97); }
.mm-sync-pop {
  position: absolute; top: calc(100% + 6px); left: 0; z-index: 70; min-width: 330px; max-width: 90vw; box-sizing: border-box;
  background: #fff; color: #1e293b; border: 1px solid #e2e8f0; border-radius: 8px;
  box-shadow: 0 8px 24px rgba(15, 23, 42, 0.14); padding: 8px 10px; text-align: left; cursor: default;
  white-space: normal;
}
.mm-sync-pop-head { font-size: 11px; color: #64748b; font-weight: 500; margin-bottom: 6px; line-height: 1.4; }
.mm-sync-pop-row { display: flex; align-items: center; gap: 8px; padding: 4px 0; font-size: 12px; border-top: 1px solid #f1f5f9; }
.mm-sync-pop-row .mm-sync-dot { width: 8px; height: 8px; }
.mm-sync-pop-name { font-weight: 700; min-width: 92px; }
.mm-sync-pop-time { font-weight: 500; color: #334155; white-space: nowrap; }
.mm-sync-pop-ago { margin-left: auto; padding-left: 10px; font-weight: 600; font-size: 11px; white-space: nowrap; }
.mm-sync-fresh-text { color: #047857; }
.mm-sync-stale-text { color: #b45309; }
.mm-sync-never-text { color: #64748b; }
.mm-filter-bar { border-top: 1px solid #e2e8f0; }
.mm-filter-row { align-items: flex-end; }
.mm-metrics-filter { min-width: 180px; }
.mm-metrics-btn { border-color: var(--rd-header-blue, #1e2a8a) !important; background: #eef2ff !important; }
.mm-metrics-btn .rd-dropdown-summary { font-weight: 700; color: var(--rd-header-blue, #1e2a8a); }
.mm-metrics-panel {
  min-width: 220px; box-sizing: border-box; padding: 6px 4px;
  max-height: none !important; overflow: visible !important; z-index: 60;
}
/* rd-option is width:100% + padding: without border-box it spills out of the panel */
.mm-metrics-panel .rd-option, .mm-metrics-panel .mm-metrics-group { box-sizing: border-box; width: 100%; }
.mm-metrics-quick { display: flex; gap: 12px; padding: 4px 12px 6px; border-bottom: 1px solid #eef2f7; }
.mm-metrics-quick button {
  border: none; background: none; color: var(--rd-header-blue, #1e2a8a);
  font-size: 12px; font-weight: 600; cursor: pointer; padding: 2px 0;
}
.mm-metrics-quick button:hover { text-decoration: underline; }
/* Checklist sections: a coloured header bar per group so PERFORMANCE and EXTRANET read as
   separate sections, not as options. Blue = performance (old MMT data), purple = extranet. */
.mm-metrics-section + .mm-metrics-section { margin-top: 8px; padding-top: 8px; border-top: 1px dashed #cbd5e1; }
.mm-metrics-group {
  display: flex; align-items: center; gap: 8px; margin: 2px 0 4px; padding: 7px 10px; border-radius: 6px;
  font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; cursor: pointer; white-space: nowrap;
}
.mm-metrics-group-perf { background: #e0e7ff; color: #1e2a8a; border-left: 4px solid #1e2a8a; }
.mm-metrics-group-ext { background: #ede9fe; color: #5b21b6; border-left: 4px solid #7c3aed; }
.mm-metrics-count { margin-left: auto; font-size: 10px; font-weight: 700; letter-spacing: 0; opacity: 0.8; }
.mm-metrics-panel .mm-metrics-option { padding-left: 22px; }

/* Metric filters: ONE full-width panel. A tab per ticked extranet metric (badge = filters in use
   on that tab) and one row with the selected metric's filters, so nothing leaves gaps and the
   panel stays ~2 rows high however many metrics are ticked. */
.mm-mf-panel { margin: 10px 0 12px; border: 1px solid #e4dcfb; border-radius: 10px; background: #fcfbff; }
.mm-mf-tabs {
  display: flex; align-items: center; gap: 6px; padding: 8px 12px; border-bottom: 1px solid #ede9fe;
  overflow-x: auto; white-space: nowrap; scrollbar-width: thin;
}
.mm-mf-heading { font-size: 11px; font-weight: 800; letter-spacing: 0.06em; text-transform: uppercase; color: #5b21b6; margin-right: 6px; flex-shrink: 0; }
.mm-mf-tab {
  display: inline-flex; align-items: center; gap: 6px; flex-shrink: 0; border: 1px solid #ddd6fe; background: #fff;
  color: #4c1d95; border-radius: 999px; padding: 4px 12px; font-size: 12px; font-weight: 600; cursor: pointer; font-family: inherit;
}
.mm-mf-tab:hover { background: #f5f3ff; }
.mm-mf-tab-active, .mm-mf-tab-active:hover { background: #7c3aed; border-color: #7c3aed; color: #fff; }
.mm-mf-badge {
  display: inline-flex; align-items: center; justify-content: center; min-width: 16px; height: 16px; padding: 0 4px;
  box-sizing: border-box; border-radius: 999px; background: #7c3aed; color: #fff; font-size: 10px; font-weight: 700;
}
.mm-mf-tab-active .mm-mf-badge { background: #fff; color: #6d28d9; }
.mm-mf-clear {
  margin-left: auto; flex-shrink: 0; border: none; background: none; color: #6d28d9; font-size: 12px; font-weight: 600;
  cursor: pointer; padding: 0 0 0 12px; font-family: inherit;
}
.mm-mf-clear:hover { text-decoration: underline; }
.mm-mf-row { display: flex; flex-wrap: wrap; gap: 10px 12px; padding: 10px 12px 12px; align-items: flex-end; }
.mm-mf-row .rd-filter-group { width: 170px; }
.mm-error {
  margin: 10px 24px 0; padding: 8px 12px; border-radius: 6px; background: #fef2f2;
  border: 1px solid #fecaca; color: #b91c1c; font-size: 13px;
}
.mm-note { margin: 8px 24px 0; font-size: 12px; color: #64748b; }

.mm-matrix-section { padding-top: 12px; }
/* The MMT Dashboard pins header rows 2 and 3 at fixed 28px / 50px. Here the extranet headers can
   wrap to 2 lines, so the real row heights differ and row 3 (MMT / GOIBIBO) got pushed down,
   leaving a gap under RATING. The offsets are measured from the real rows instead (--mm-top2/3). */
.mmt-table.mm-dyn-sticky thead .mmt-th-metric-group { top: var(--mm-top2, 28px) !important; }
.mmt-table.mm-dyn-sticky thead .mmt-th-subcol { top: var(--mm-top3, 50px) !important; }
/* The table itself uses the MMT Dashboard's own classes from Dashboard.css
   (rd-table occ-table mmt-table, rd-th-fixed, occ-th-group, mmt-th-metric-group, mmt-th-subcol,
   occ-td-t / occ-td-a, mmt-growth-up / -down, rd-total-td), so colours match it exactly.
   The rules below only cover the extranet columns, which the MMT Dashboard doesn't have. */
.mm-ext-head { white-space: normal !important; line-height: 1.2; padding: 4px 4px !important; cursor: pointer; }
/* clicking a hotel name opens its detail page */
.mmt-table td.mm-hotel-link { cursor: pointer; color: #1e2a8a; }
.mmt-table td.mm-hotel-link:hover { text-decoration: underline; }
.mm-ext-td {
  text-align: center; font-size: 12.5px; padding: 6px 6px; border-right: 1px solid #eef0f2;
  vertical-align: middle; font-variant-numeric: tabular-nums;
}
.mm-ext-text { text-align: left; white-space: normal; line-height: 1.35; word-break: break-word; }
.mm-ext-td.mm-ext-num { text-align: center; white-space: nowrap; }
/* Sponsored / VDI: one fixed-height line per campaign / rule so the columns line up */
.mm-stack-td { vertical-align: top; padding-top: 8px; padding-bottom: 8px; }
.mm-stack { display: flex; flex-direction: column; gap: 4px; }
.mm-stack-line {
  height: 22px; line-height: 22px; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; text-align: left;
}
.mm-stack-line.mm-stack-center { text-align: center; }
.mm-stack-line.mm-stack-num { text-align: center; font-variant-numeric: tabular-nums; }
.mm-empty { text-align: center; color: #64748b; padding: 28px 12px; }
.mm-muted { color: #cbd5e1; }
.mm-pending { display: inline-block; min-width: 28px; padding: 1px 8px; border-radius: 6px; font-weight: 700; }
.mm-pending-zero { background: #dcfce7; color: #166534; }
.mm-pending-some { background: #fef3c7; color: #92400e; }
.mmt-table td.mmt-growth-up, .mmt-table td.mmt-growth-down { text-align: center; font-size: 12.5px; font-weight: 600; padding: 6px 4px; }

.mm-badge { display: inline-block; padding: 2px 9px; border-radius: 999px; font-size: 12px; font-weight: 600; white-space: nowrap; }
.mm-badge-green { background: #dcfce7; color: #166534; }
.mm-badge-blue { background: #dbeafe; color: #1e40af; }
.mm-badge-amber { background: #fef3c7; color: #92400e; }
.mm-badge-grey { background: #e2e8f0; color: #475569; }
.mm-badge-neutral { background: #ede9fe; color: #5b21b6; }
.mm-score { display: inline-block; min-width: 52px; text-align: center; padding: 2px 8px; border-radius: 6px; font-weight: 700; }
.mm-score-top { background: #bbf7d0; color: #14532d; }
.mm-score-good { background: #dcfce7; color: #166534; }
.mm-score-mid { background: #fef9c3; color: #854d0e; }
.mm-score-low { background: #fee2e2; color: #991b1b; }

.mm-list-td { text-align: left; }
.mm-chips-wrap { display: flex; flex-wrap: wrap; gap: 6px; align-items: center; min-width: 0; max-width: 100%; }
.mm-chip {
  display: inline-flex; align-items: center; gap: 6px; max-width: 100%; min-width: 0; box-sizing: border-box;
  padding: 3px 10px; border-radius: 999px; border: 1px solid transparent; font-size: 12px; line-height: 1.4; white-space: nowrap;
}
.mm-chip-text { overflow: hidden; text-overflow: ellipsis; min-width: 0; }
.mm-chip-value { flex-shrink: 0; font-weight: 700; font-size: 11px; padding: 1px 7px; border-radius: 999px; background: rgba(255, 255, 255, 0.85); }
.mm-chip-blue { background: #e0e7ff; border-color: #c7d2fe; color: #1e3a8a; }
.mm-chip-amber { background: #fef3c7; border-color: #fde68a; color: #92400e; }
.mm-chip-purple { background: #ede9fe; border-color: #ddd6fe; color: #5b21b6; }
.mm-chip-green { background: #dcfce7; border-color: #bbf7d0; color: #166534; }
.mm-chip-slate { background: #f1f5f9; border-color: #e2e8f0; color: #1e293b; }
.mm-chip-more {
  border: 1px dashed #94a3b8; background: #fff; color: var(--rd-header-blue, #1e2a8a);
  border-radius: 999px; padding: 3px 10px; font-size: 12px; font-weight: 600; cursor: pointer;
}
.mm-chip-more:hover { background: #eef2ff; }

/* Desktop: fit Metrics, Hotel, Location, Agent, Search, Date range, the toggles AND the
   Refresh / Sync / Clear buttons on one row (1920px screens). On narrower laptops the date range,
   toggles and buttons wrap together to a second row instead of the buttons sitting alone. */
@media (min-width: 769px) {
  .mm-filter-row { gap: 12px; }
  .mm-filter-row > .rd-filter-group { width: 148px; }
  .mm-filter-row > .mm-metrics-filter { width: 158px; min-width: 0; }
  .mm-filter-row > .rd-search-group { width: 200px; }
  .mm-filter-row > .mm-row-tail { display: flex; align-items: flex-start; gap: 12px; flex: 1 1 auto; flex-wrap: nowrap; }
  .mm-row-tail > .rd-date-range-group { width: auto; }
  .mm-row-tail > .rd-btn-toggle { flex-shrink: 0; }
  .mm-row-tail > .rd-actions { margin-left: auto; }
  .mm-row-tail > .rd-btn-toggle { padding: 8px 12px; }
  .mm-filter-row .rd-actions { gap: 6px; flex-wrap: nowrap; }
  .mm-filter-row .rd-actions .rd-btn-sync,
  .mm-filter-row .rd-actions .rd-btn-clear { padding: 7px 10px; font-size: 13px; white-space: nowrap; }
}

@media (max-width: 768px) {
  .mm-row-tail { display: contents; }
  /* MOBILE — same pattern as the other dashboards: collapsible "Filters" panel (shared
     .occ-mobile-filter-toggle / .occ-filter-body styles), 52px gap on the left for the floating
     menu button, the page scrolls and the table keeps its own scroll area. The table itself
     uses the MMT Dashboard classes, so Dashboard.css already unpins the rating columns on phones
     and keeps only the hotel name pinned. */
  .mm-page { overflow-y: auto; -webkit-overflow-scrolling: touch; }
  .mm-page > * { flex-shrink: 0; } /* the page scrolls instead of squashing the header */
  .mm-header { padding: 12px 14px 8px 52px; min-height: 50px; box-sizing: border-box; }
  .mm-title { font-size: 14px; line-height: 26px; }
  .mm-sync-line { gap: 6px; margin-top: 6px; }
  .mm-sync-pill { font-size: 10.5px; padding: 2px 8px; }
  .mm-error, .mm-note { margin-left: 12px; margin-right: 12px; font-size: 12px; }
  .mm-page .occ-filter-body:not(.occ-filter-body-collapsed) { max-height: none; overflow: visible; }
  .mm-metrics-filter { grid-column: 1 / -1; min-width: 0; width: 100%; }
  .mm-mf-panel { margin: 4px 0 10px; }
  .mm-mf-tabs { padding: 8px 10px; -webkit-mask-image: linear-gradient(to right, #000 82%, transparent); mask-image: linear-gradient(to right, #000 82%, transparent); padding-right: 28px; }
  .mm-mf-row { display: grid; grid-template-columns: 1fr 1fr; gap: 8px 10px; padding: 10px; }
  .mm-mf-row .rd-filter-group { width: 100%; }
  .mm-page .rd-actions { display: flex; flex-wrap: wrap; gap: 8px; }
  .mm-page .rd-matrix-section { flex: 0 0 auto; height: 78vh; min-height: 320px; padding: 10px 12px 12px; }
  /* narrower pinned hotel column; long names wrap onto 2 lines */
  .mm-page .mmt-table col.mm-col-hotel { width: 140px !important; }
  .mm-page .mmt-table th.rd-th-fixed:not(.rd-side),
  .mm-page .mmt-table td.rd-td-fixed:not(.rd-side) {
    width: 140px !important; min-width: 140px; max-width: 140px;
    white-space: normal; line-height: 1.25;
  }
  .mm-page .mmt-table th.rd-th-fixed:not(.rd-side) { padding-left: 10px; padding-right: 6px; }
  .mm-ext-td { font-size: 11px; padding: 5px 4px; }
  .mm-ext-head { font-size: 9px !important; }
  .mm-badge { font-size: 10.5px; padding: 1px 7px; }
  .mm-chip { font-size: 11px; padding: 2px 8px; }
}
`;


// ---------------------------------------------------------------- helpers
function apiUrl(path) {
  let username = "";
  try {
    username = localStorage.getItem("loggedInUser") || "";
  } catch {
    username = "";
  }
  if (!username) return path;
  const separator = path.includes("?") ? "&" : "?";
  return `${path}${separator}username=${encodeURIComponent(username)}`;
}
const SYNC_ALLOWED_ROLES = new Set(["SUPER ADMIN", "ADMIN", "OTA MANAGER"]);
function canUseSync() {
  try {
    return SYNC_ALLOWED_ROLES.has((localStorage.getItem("loggedInRole") || "").trim().toUpperCase());
  } catch {
    return false;
  }
}
const norm = (v) => String(v ?? "").trim().toUpperCase();
function toNumber(value) {
  if (value === null || value === undefined || value === "") return null;
  const n = Number(String(value).replace(/[%,₹\s]/g, ""));
  return Number.isFinite(n) ? n : null;
}
function shortDate(iso) {
  const d = new Date(`${String(iso).slice(0, 10)}T00:00:00`);
  return Number.isNaN(d.getTime()) ? String(iso) : d.toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
function prettify(value) {
  if (value === null || value === undefined) return "";
  const text = String(value).trim();
  if (!text) return "";
  if (/^n\/?a$/i.test(text)) return "N/A"; // "NA" / "n/a" -> N/A (not "Na")
  if (/^[A-Za-z0-9]+([_ ][A-Za-z0-9]+)*$/.test(text) && (text === text.toUpperCase() || text.includes("_") || text === text.toLowerCase())) {
    // NOT_ENROLLED -> Not Enrolled, enrolled -> Enrolled
    return text.toLowerCase().split(/[_ ]/).map((w) => (w ? w[0].toUpperCase() + w.slice(1) : w)).join(" ");
  }
  return text;
}
// Formats one extranet value for display AND for the filter options (same text in both places).
function formatValue(value, format) {
  if (value === null || value === undefined || String(value).trim() === "") return "";
  const raw = String(value).trim();
  const n = toNumber(raw);
  switch (format) {
    case "percent":
      return n === null ? raw : `${n.toLocaleString("en-IN", { maximumFractionDigits: 2 })}%`;
    case "month": {
      // "2024-10-01" -> "Oct 2024" (the month a VDI rule is for)
      const m = raw.match(/^(\d{4})-(\d{2})/);
      if (!m) return raw;
      const d = new Date(Number(m[1]), Number(m[2]) - 1, 1);
      return d.toLocaleDateString("en-GB", { month: "short", year: "numeric" });
    }
    case "currency":
      return n === null ? raw : `₹${n.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
    case "decimal2":
      return n === null ? raw : n.toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    case "int":
    case "number":
    case "score":
      return n === null ? raw : n.toLocaleString("en-IN", { maximumFractionDigits: 2 });
    case "badge":
      return prettify(raw);
    case "datetime": {
      if (/^\d{4}-\d{2}-\d{2}([ T]\d{2}:\d{2}(:\d{2})?)?/.test(raw)) {
        const d = new Date(raw.length === 10 ? `${raw}T00:00:00` : raw.replace(" ", "T"));
        if (!Number.isNaN(d.getTime())) {
          return raw.length === 10
            ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "numeric" })
            : d.toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });
        }
      }
      return raw;
    }
    default:
      return raw;
  }
}
// "[2026-12-24, 2026-12-31]" / "a; b" / "a | b" -> ["a", "b"]
function splitList(value) {
  if (value === null || value === undefined) return [];
  return String(value)
    .replace(/^\s*\[|\]\s*$/g, "")
    .split(/[,;|\n]/)
    .map((s) => s.replace(/^["'\s]+|["'\s]+$/g, ""))
    .filter(Boolean);
}
function fieldValues(row, field) {
  if (field.split) return splitList(row[field.id]).map((v) => formatValue(v, field.format)).filter(Boolean);
  const v = formatValue(row[field.id], field.format);
  return v ? [v] : [];
}
function badgeTone(value) {
  const v = String(value || "").toLowerCase();
  if (/deactivat|reject|expired|inactive|pending/.test(v)) return "mm-badge-amber"; // before the "active" check
  if (/^(ended|completed|stopped|closed|finished|fulfilled|partial[ _]fulfilled)$/.test(v)) return "mm-badge-grey"; // finished campaigns / rules
  if (/^(not|non|in|un)[ _-]?(enrolled|enroled|eligible|active|live)|^no$|paused|expired|disabled|off/.test(v)) return "mm-badge-amber";
  if (/enrolled|eligible|active|live|^yes$|^on$|running|enabled/.test(v)) return "mm-badge-green";
  if (/gold|platinum|premium|silver/.test(v)) return "mm-badge-blue";
  if (/no data|not available|unknown|n\/a/.test(v)) return "mm-badge-grey";
  return "mm-badge-neutral";
}
function scoreTone(n) {
  if (n === null) return "";
  if (n >= 90) return "mm-score-top";
  if (n >= 75) return "mm-score-good";
  if (n >= 50) return "mm-score-mid";
  return "mm-score-low";
}
function tierTone(tier) {
  const t = String(tier || "").toLowerCase();
  if (/platinum|premium|1/.test(t)) return "mm-chip-purple";
  if (/gold|2/.test(t)) return "mm-chip-amber";
  if (/silver|3/.test(t)) return "mm-chip-blue";
  return "mm-chip-slate";
}
function ratingColor(rating) {
  // MMT and Goibibo ratings are out of 5 (same colour code as the MMT Dashboard)
  const v = Math.round(Number(rating) * 10) / 10;
  if (!(v > 0)) return "#FFFFFF";
  if (v >= 4.6) return "#66BB6A";
  if (v >= 4.1) return "#A5D6A7";
  if (v >= 3.1) return "#FFF59D";
  return "#EF9A9A";
}
const NAME_STOPWORDS = new Set([
  "hotel", "resort", "resorts", "inn", "retreat", "retreats", "residency", "palace", "castle", "view", "house",
  "camp", "camps", "lodge", "villa", "villas", "near", "by", "the", "a", "an", "at", "of", "and", "stay", "stays",
  "homestay", "homestays", "guest", "guesthouse", "cottage", "cottages", "farm", "farms", "point", "road",
  "international", "airport", "behind", "opp", "opposite", "apartment", "apartments", "studio",
]);
function nameWords(str) {
  const words = String(str || "").toLowerCase().replace(/[^a-z0-9\s]/g, " ").split(/\s+/).filter(Boolean);
  const filtered = words.filter((w) => !NAME_STOPWORDS.has(w));
  return filtered.length ? filtered : words;
}
function nameScore(a, b) {
  const wa = new Set(nameWords(a));
  const wb = new Set(nameWords(b));
  if (!wa.size || !wb.size) return 0;
  let n = 0;
  wa.forEach((w) => {
    if (wb.has(w)) n += 1;
  });
  return n / Math.min(wa.size, wb.size);
}

// ---------------------------------------------------------------- metric definitions
// OLD MMT Dashboard metrics (gommt_db.trends via /api/mmt/summary). `trend` = the exact `metric`
// value in that table. Totals: same basis as the MMT Dashboard (ASP = total Revenue / total RN).
const PERF_METRICS = [
  { key: "room_nights", label: "Room Nights", abbr: "RN", trend: "Room Nights", format: "int" },
  { key: "revenue", label: "Revenue", abbr: "REV", trend: "Revenue", format: "currency" },
  { key: "asp", label: "ASP", abbr: "ASP", trend: "ASP", format: "currency" },
  { key: "visits", label: "Property Visits", abbr: "VISITS", trend: "Property Visits", format: "int" },
  { key: "conversion", label: "Conversion", abbr: "CONV%", trend: "Conversion", format: "percent2" },
];
function formatPerf(value, format) {
  const num = Number(value) || 0;
  if (format === "currency") return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (format === "percent2") return `${num.toFixed(2)}%`;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}

// NEW extranet metrics (/api/mmt-metrics/data). `fields` = the columns from "MMT Dashboard
// Requirements" (each one is also a filter while the metric is ticked). `cols` = how the metric
// appears in the table: "value" (one value per hotel), "list" (all values as chips), "promotions".
const EXT_METRICS = [
  {
    key: "promotions", label: "Promotions",
    fields: [
      { id: "promotion_name", label: "Promotion Name", format: "text" },
      { id: "tier", label: "Tier", format: "badge" },
      { id: "max_discount", label: "Max Discount", format: "percent" },
      { id: "room_nights", label: "Room Night", format: "int" },
      { id: "status", label: "Status", format: "badge" },
    ],
    cols: [{ id: "promotions", label: "Promotions", kind: "promotions" }],
  },
  {
    key: "spotlight", label: "Spotlight",
    fields: [
      { id: "enrollment_status", label: "Enrollment Status", format: "badge" },
      { id: "eligibility_status", label: "Eligibility Status", format: "badge" },
      { id: "visit_boost_pct", label: "Visit Boost Pct", format: "percent" },
      { id: "performance_lookback", label: "Performance Lookback", format: "text" },
    ],
    cols: [
      { id: "sp_enroll", label: "Enrollment Status", kind: "value", field: "enrollment_status" },
      { id: "sp_elig", label: "Eligibility Status", kind: "value", field: "eligibility_status" },
      { id: "sp_boost", label: "Visit Boost Pct", kind: "value", field: "visit_boost_pct" },
      { id: "sp_lookback", label: "Performance Lookback", kind: "value", field: "performance_lookback" },
    ],
  },
  {
    key: "content", label: "Content",
    fields: [
      { id: "total_score", label: "Total Score", format: "score" },
      { id: "photos_pending", label: "Photos Pending", format: "int", pending: true },
      { id: "amenities_pending", label: "Amenities Pending", format: "int", pending: true },
      { id: "properties_pending", label: "Properties Pending", format: "int", pending: true },
      { id: "policies_pending", label: "Policies Pending", format: "int", pending: true },
      { id: "boost", label: "Boost", format: "number" },
    ],
    cols: [
      { id: "ct_score", label: "Total Score", kind: "value", field: "total_score" },
      { id: "ct_photos", label: "Photos Pending", kind: "value", field: "photos_pending" },
      { id: "ct_amenities", label: "Amenities Pending", kind: "value", field: "amenities_pending" },
      { id: "ct_properties", label: "Properties Pending", kind: "value", field: "properties_pending" },
      { id: "ct_policies", label: "Policies Pending", kind: "value", field: "policies_pending" },
      { id: "ct_boost", label: "Boost", kind: "value", field: "boost" },
    ],
  },
  {
    key: "cancellation", label: "Cancellation",
    fields: [
      { id: "rules", label: "Rules", format: "text" },
      { id: "blackout_dates", label: "Black Out Dates", format: "datetime", split: true },
    ],
    cols: [
      { id: "cx_rules", label: "Rules", kind: "list", field: "rules" },
      { id: "cx_blackout", label: "Black Out Dates", kind: "list", field: "blackout_dates" },
    ],
  },
  {
    key: "inclusions", label: "Inclusions",
    fields: [
      { id: "description", label: "Description", format: "text" },
      { id: "status", label: "Status", format: "badge" },
    ],
    cols: [{ id: "inc_list", label: "Description", kind: "inclusions" }],
  },
  {
    key: "lmr", label: "LMR",
    fields: [
      { id: "status", label: "Status", format: "badge" },
      { id: "start_time", label: "Start Time", format: "datetime" },
    ],
    cols: [
      { id: "lmr_status", label: "Status", kind: "value", field: "status" },
      { id: "lmr_start", label: "Start Time", kind: "value", field: "start_time" },
    ],
  },
  {
    // Sponsored listings: one row per campaign (mmt_extranet.sponsored_listing). Each column shows
    // one line per campaign, in the same order (highest amount spent first), so the lines line up.
    key: "sponsored", label: "Sponsored",
    fields: [
      { id: "campaign_status", label: "Campaign Status", format: "badge" },
      { id: "amount_spent", label: "Amount Spent", format: "currency" },
      { id: "roas", label: "ROAS", format: "decimal2" },
    ],
    stackSort: (a, b) => (toNumber(b.amount_spent) || 0) - (toNumber(a.amount_spent) || 0),
    stackTitle: (r) => r.campaign_name || "",
    cols: [
      { id: "sp_status", label: "Campaign Status", kind: "stack", field: "campaign_status" },
      { id: "sp_spent", label: "Amount Spent", kind: "stack", field: "amount_spent" },
      { id: "sp_roas", label: "ROAS", kind: "stack", field: "roas" },
    ],
  },
  {
    // VDI: one row per VDI rule (mmt_extranet.vdi_rules). The table keeps one rule PER MONTH going
    // back years, so: Month (from rule_start_date) shows which month a rule is for, newest first;
    // State = switched on in the extranet (active / pending / inactive); Status = whether its dates
    // are current (e.g. expired). An "Active" state with an "Expired" status is an old month's rule.
    key: "vdi", label: "VDI",
    fields: [
      { id: "rule_start_date", label: "Month", format: "month" },
      { id: "state", label: "State", format: "badge" },
      { id: "status", label: "Status", format: "badge" },
      { id: "description", label: "Description", format: "text" },
    ],
    stackSort: (a, b) =>
      String(b.rule_start_date || "").localeCompare(String(a.rule_start_date || "")) ||
      String(a.description || "").localeCompare(String(b.description || "")),
    stackTitle: (r) => [formatValue(r.rule_start_date, "month"), r.description].filter(Boolean).join(" · "),
    cols: [
      { id: "vdi_month", label: "Month", kind: "stack", field: "rule_start_date" },
      { id: "vdi_state", label: "State", kind: "stack", field: "state" },
      { id: "vdi_status", label: "Status", kind: "stack", field: "status" },
      { id: "vdi_desc", label: "Description", kind: "stack", field: "description", wide: true },
    ],
  },
];
const EXT_BY_KEY = Object.fromEntries(EXT_METRICS.map((m) => [m.key, m]));
const PERF_BY_KEY = Object.fromEntries(PERF_METRICS.map((m) => [m.key, m]));

// TASKS — the old "MMT Task" (Visits / Conversion) has been REMOVED from the Task page; it is
// covered by the MMT Extranet Task (MMT_EXTRANET_TASK_SOURCE below). This definition is still used
// by Task.jsx for the MMT Extranet Task's Rule 0: the Property Visits metric and the API it is read
// from (Property Visits REVX vs COMP summed over the last 15 days with data).
export const MMT_METRICS_TASK_SOURCE = {
  dashboard: "MMT Metrics",
  section: "Performance",
  endpoint: "/api/mmt/summary",
  metrics: [
    { metric: PERF_BY_KEY.visits.trend, taskKey: "Property Visits", label: "Property Visits", task: "Increase MMT Visits Above Comp Set Average" },
    { metric: PERF_BY_KEY.conversion.trend, taskKey: "Conversion", label: "Conversion", task: "Increase MMT Conversion Above Comp Set Average" },
  ],
};

// TASKS — the "MMT Extranet Task" on the Task page is generated FROM THIS DASHBOARD's extranet
// metrics, following "MMT – Task Creation Logic":
//   Rule 0 (checked in Task.jsx with the MMT Task's own data): a property is eligible only if its
//           Property Visits are BELOW comp visits over the last 15 days (equal = not eligible).
//   Then each check below decides: create a task (create: true) or not (create: false).
//   A check returns null when its input is missing — then NO task is created ("a missing input is
//   not treated as Yes or No"), e.g. the hotel has no Content row, or a table failed to load.
// `key` is part of every task's id (e.g. "REVX_0054::mmtext::Spotlight"): NEVER change it, or the
// existing tasks (with their status, comments and closed state) would look brand new.
// m = { spotlight:[rows], content:[rows], ... } for one hotel (latest data, as on this dashboard);
// covered = the extranet sync has this hotel at all (it has a Content or Spotlight row), so "no
// promotion / inclusion / VDI / campaign rows" really means none, not "not scraped";
// loaded(metric) = that metric's table loaded without an error.
const isOnValue = (v) => {
  const s = String(v ?? "").trim().toLowerCase();
  return !!s && !/^(not|non|in|un|de)[ _-]?/.test(s) && !/^(no|false|off|n\/a|na|none)$/.test(s);
};
const isRunningValue = (v) => isOnValue(v) && /active|live|running|^on$|^yes$|^true$/.test(String(v).trim().toLowerCase());
// "Free cancellation until 72h (3d) before check-in" -> 72 · "...until 0h" / "...till check-in" -> 0
function freeCancellationHours(text) {
  const t = String(text || "").toLowerCase();
  if (!/free/.test(t)) return null;
  const h = t.match(/(\d+(?:\.\d+)?)\s*(?:h|hr|hrs|hour|hours)\b/);
  if (h) return Number(h[1]);
  const d = t.match(/(\d+(?:\.\d+)?)\s*(?:d|day|days)\b/);
  if (d) return Number(d[1]) * 24;
  if (/(until|till|up ?to)\s*(the\s*)?(check-?in|arrival)/.test(t)) return 0;
  return null;
}
const shortText = (v, n = 60) => {
  const s = String(v || "").trim();
  return s.length > n ? `${s.slice(0, n - 1)}…` : s;
};
export const MMT_EXTRANET_TASK_SOURCE = {
  dashboard: "MMT Metrics",
  section: "Extranet",
  endpoint: "/api/mmt-metrics/data",
  checks: [
    {
      // Enrolled = Yes AND Eligible = Yes -> no task; otherwise task
      key: "Spotlight", label: "Spotlight", task: "Get the property enrolled in MMT Spotlight and eligible",
      evaluate: (m) => {
        const r = (m.spotlight || [])[0];
        if (!r || (!r.enrollment_status && !r.eligibility_status)) return null;
        const ok = isOnValue(r.enrollment_status) && isOnValue(r.eligibility_status);
        return { create: !ok, state: `${prettify(r.enrollment_status) || "—"} · ${prettify(r.eligibility_status) || "—"}` };
      },
    },
    {
      // Content score = 100 -> no task; otherwise task
      key: "Content", label: "Content", task: "Bring the MMT content score to 100",
      evaluate: (m) => {
        const score = toNumber((m.content || [])[0]?.total_score);
        if (score === null) return null;
        return { create: score < 100, state: `Score ${formatValue(score, "score")}` };
      },
    },
    {
      // Free cancellation until 0h before check-in -> no task; otherwise task.
      // No cancellation policy at all (empty rule) -> task "Add a cancellation policy" — only when
      // the extranet sync has this hotel and the Cancellation table loaded (agreed with the team).
      key: "Cancellation", label: "Cancellation", task: "Offer free cancellation until 0 hours before check-in",
      evaluate: (m, covered, loaded) => {
        const rows = (m.cancellation || []).filter((r) => String(r.rules || "").trim());
        if (!rows.length) {
          if (!covered || !loaded("cancellation")) return null;
          return { create: true, state: "No cancellation policy", task: "Add a cancellation policy on MMT" };
        }
        const zero = rows.some((r) => freeCancellationHours(r.rules) === 0);
        return { create: !zero, state: zero ? "Free until 0h" : shortText(rows[0].rules) };
      },
    },
    {
      // Any active inclusion -> no task; none active -> task
      key: "Inclusions", label: "Inclusions", task: "Add at least one active inclusion on MMT",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("inclusions")) return null;
        const active = (m.inclusions || []).filter((r) => isRunningValue(r.status)).length;
        return { create: active === 0, state: active ? `${active} active` : "No active inclusion" };
      },
    },
    {
      // VDI — ONLY the CURRENT MONTH's rule can create a task (agreed with the team):
      //   current month's rule is Pending / Inactive  -> task
      //   current month's rule is Active              -> no task
      //   no rule for the current month               -> no task (past / future months' rules,
      //                                                  or no VDI at all, never create a task)
      // "Current month's rule" = its rule_start_date is in the current month, or its date range
      // includes today. Pending counts as inactive.
      key: "VDI", label: "VDI", task: "Start a VDI offer on MMT for this month",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("vdi")) return null;
        const now = new Date();
        const p2 = (n) => String(n).padStart(2, "0");
        const today = `${now.getFullYear()}-${p2(now.getMonth() + 1)}-${p2(now.getDate())}`;
        const thisMonth = today.slice(0, 7);
        const monthLabel = formatValue(`${thisMonth}-01`, "month");
        const current = (m.vdi || []).filter((r) => {
          const start = String(r.rule_start_date || "").slice(0, 10);
          const end = String(r.rule_end_date || "").slice(0, 10);
          return start.slice(0, 7) === thisMonth || (start && end && start <= today && today <= end);
        });
        if (current.some((r) => isRunningValue(r.state))) {
          return { create: false, state: `Active for ${monthLabel}` };
        }
        if (!current.length) return null; // no rule for the current month -> no task
        return { create: true, state: `Not active for ${monthLabel} (${prettify(current[0].state) || "—"})` };
      },
    },
    {
      // At least one promotion running -> no task; none -> task
      key: "Promotions", label: "Promotions", task: "Run at least one promotion on MMT",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("promotions")) return null;
        const running = (m.promotions || []).filter((r) => isRunningValue(r.status)).length;
        return { create: running === 0, state: running ? `${running} running` : "No promotion running" };
      },
    },
    {
      // LMR status = Off -> task; otherwise no task
      key: "LMR", label: "LMR", task: "Turn MMT LMR (last-minute rates) on",
      evaluate: (m) => {
        const r = (m.lmr || [])[0];
        if (!r || !String(r.status ?? "").trim()) return null;
        const off = /^(off|inactive|disabled|no|false|stopped|paused)$/.test(String(r.status).trim().toLowerCase());
        return { create: off, state: `Status ${prettify(r.status)}` };
      },
    },
    {
      // Sponsor (campaign) status = Live -> no task; otherwise task.
      // FULFILLED and PARTIAL_FULFILLED are finished campaigns (agreed with the team) -> task.
      key: "Sponsor", label: "Sponsor", task: "Make an MMT sponsored campaign Live",
      evaluate: (m, covered, loaded) => {
        if (!covered || !loaded("sponsored")) return null;
        const rows = m.sponsored || [];
        const live = rows.some((r) => /^live$/.test(String(r.campaign_status || "").trim().toLowerCase()));
        return { create: !live, state: live ? "Live" : rows.length ? `No live campaign (${prettify(rows[0].campaign_status) || "—"})` : "No campaign" };
      },
    },
  ],
};
const ALL_METRIC_LABEL = { ...Object.fromEntries(PERF_METRICS.map((m) => [m.key, m.label])), ...Object.fromEntries(EXT_METRICS.map((m) => [m.key, m.label])) };
const fieldOf = (metricKey, fieldId) => EXT_BY_KEY[metricKey].fields.find((f) => f.id === fieldId);

const DEFAULT_SELECTION = PERF_METRICS.map((m) => m.key); // = the old MMT Dashboard
const STORAGE_KEY = "revx_mmt_metrics_selection_v2";
const MAX_DATE_COLUMNS = 31;
// column widths: same as the MMT Dashboard (property 210, rating / metric columns 78, totals 98)
const HOTEL_W = 210;
const SIDE_W = 78;
const METRIC_W = 78;
const TOTAL_W = 98;
const extColWidth = (c) => (c.kind === "promotions" ? 300 : c.kind === "value" ? 120 : c.kind === "stack" ? (c.wide ? 280 : 130) : 240);
// "TOTAL" header selector per metric, same choices and defaults as the MMT Dashboard
const DEFAULT_TOTAL_MODES = { room_nights: "sum", revenue: "sum", asp: "revrn", visits: "sum", conversion: "average" };
const SYNC_POLL_MS = 2 * 60 * 1000;
const SYNC_WAIT_MS = 16 * 60 * 1000;

// ---------------------------------------------------------------- "Last sync" pills
const SYNC_STALE_HOURS = 36; // a daily sync that is more than 36 h old was missed at least once
function parseSyncDate(value) {
  if (!value) return null;
  const text = String(value);
  const d = /^\d{4}-\d{2}-\d{2}$/.test(text) ? new Date(`${text}T00:00:00`) : new Date(text);
  return Number.isNaN(d.getTime()) ? null : d;
}
function syncInfo(value) {
  const d = parseSyncDate(value);
  if (!d) return { tone: "never", when: value ? String(value) : "Not yet synced", ago: "", ms: null };
  const dateOnly = /^\d{4}-\d{2}-\d{2}$/.test(String(value));
  const sameYear = d.getFullYear() === new Date().getFullYear();
  const when = dateOnly
    ? d.toLocaleDateString("en-GB", { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }) })
    : d.toLocaleString("en-IN", { day: "2-digit", month: "short", ...(sameYear ? {} : { year: "numeric" }), hour: "2-digit", minute: "2-digit" });
  const hours = (Date.now() - d.getTime()) / 36e5;
  const days = Math.floor(hours / 24);
  const ago = hours < 1 ? "just now" : hours < 24 ? `${Math.floor(hours)}h ago` : days === 1 ? "1 day ago" : `${days} days ago`;
  return { tone: hours > SYNC_STALE_HOURS ? "stale" : "fresh", when, ago, ms: d.getTime() };
}
// One pill per data source. `details` = [[name, value], ...]: the pill shows the OLDEST of them
// (so stale data is never hidden) and opens a list with each one's own time when clicked.
function SyncPill({ label, value, details, note }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [open]);
  let shown = value;
  if (details && details.length) {
    const infos = details.map(([, v]) => syncInfo(v));
    const known = infos.filter((i) => i.ms !== null);
    shown = known.length ? details[infos.indexOf(known.reduce((a, b) => (b.ms < a.ms ? b : a)))][1] : null;
  }
  const info = syncInfo(shown);
  const expandable = details && details.length > 1;
  const neverCount = details ? details.filter(([, v]) => syncInfo(v).ms === null).length : 0;
  const partial = neverCount > 0 && neverCount < (details ? details.length : 0);
  const body = (
    <>
      <span className="mm-sync-dot" />
      <span className="mm-sync-label">{label}</span>
      <span>· {info.when}</span>
      {info.ago && <span className="mm-sync-ago">· {info.ago}</span>}
      {partial && <span className="mm-sync-ago">· {neverCount} not synced</span>}
      {expandable && <ChevronDown size={12} />}
    </>
  );
  const cls = `mm-sync-pill mm-sync-${info.tone}`;
  const title = info.tone === "stale" ? `Last sync was ${info.ago}: the daily sync may not be running.` : undefined;
  if (!expandable) return <span className={cls} title={title}>{body}</span>;
  return (
    <span ref={ref} style={{ position: "relative", display: "inline-flex" }}>
      <button type="button" className={cls} title={title} onClick={() => setOpen((o) => !o)} aria-expanded={open}>
        {body}
      </button>
      {open && (
        <div className="mm-sync-pop" role="dialog">
          {note && <div className="mm-sync-pop-head">{note}</div>}
          {details.map(([name, v]) => {
            const i = syncInfo(v);
            return (
              <div key={name} className={`mm-sync-pop-row mm-sync-${i.tone}-text`}>
                <span className="mm-sync-dot" />
                <span className="mm-sync-pop-name">{name}</span>
                <span className="mm-sync-pop-time">{i.when}</span>
                <span className="mm-sync-pop-ago">{i.ago}</span>
              </div>
            );
          })}
        </div>
      )}
    </span>
  );
}

// ---------------------------------------------------------------- small components
function FilterLabel({ label, onClear }) {
  return (
    <div className="rd-filter-label-row">
      <label className="rd-filter-label">{label}</label>
      {onClear && (
        <button onClick={onClear} title={`Clear ${label}`} className="rd-clear-btn">
          <Eraser size={12} />
        </button>
      )}
    </div>
  );
}
function MultiSelect({ label, options, selected, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const shown = useMemo(() => {
    const qq = query.trim().toLowerCase();
    return qq ? options.filter((o) => String(o).toLowerCase().includes(qq)) : options;
  }, [options, query]);
  const allShownSelected = shown.length > 0 && shown.every((o) => selected.includes(o));
  function toggle(opt) {
    onChange(selected.includes(opt) ? selected.filter((s) => s !== opt) : [...selected, opt]);
  }
  function toggleAll() {
    if (allShownSelected) onChange(selected.filter((s) => !shown.includes(s)));
    else onChange(Array.from(new Set([...selected, ...shown])));
  }
  const summary = selected.length === 0 ? "All" : selected.length === 1 ? selected[0] : `${selected.length} selected`;
  return (
    <div className="rd-filter-group" ref={ref}>
      <FilterLabel label={label} onClear={selected.length ? () => onChange([]) : null} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <div className="rd-dropdown-search-wrap">
            <Search size={13} className="rd-dropdown-search-icon" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="rd-dropdown-search-input"
            />
          </div>
          {shown.length > 0 && (
            <label className="rd-option rd-option-selectall">
              <input type="checkbox" checked={allShownSelected} onChange={toggleAll} />
              <span>Select All{query.trim() ? ` (${shown.length} shown)` : ""}</span>
            </label>
          )}
          {shown.length === 0 ? (
            <div className="rd-dropdown-empty">No matches</div>
          ) : (
            shown.map((opt) => (
              <label key={opt} className={`rd-option${selected.includes(opt) ? " rd-selected" : ""}`}>
                <input type="checkbox" checked={selected.includes(opt)} onChange={() => toggle(opt)} />
                <span>{opt}</span>
              </label>
            ))
          )}
        </div>
      )}
    </div>
  );
}
function MetricsFilter({ selected, onChange }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    function onDoc(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);
  const groups = [
    { id: "perf", label: "Performance", keys: PERF_METRICS.map((m) => m.key) },
    { id: "ext", label: "Extranet", keys: EXT_METRICS.map((m) => m.key) },
  ];
  const order = groups.flatMap((g) => g.keys);
  const ordered = (keys) => order.filter((k) => keys.includes(k));
  function toggle(key) {
    if (selected.includes(key)) {
      if (selected.length > 1) onChange(selected.filter((k) => k !== key)); // at least one stays selected
    } else onChange(ordered([...selected, key]));
  }
  function setGroup(keys, on) {
    const next = on ? ordered(Array.from(new Set([...selected, ...keys]))) : selected.filter((k) => !keys.includes(k));
    onChange(next.length ? next : DEFAULT_SELECTION);
  }
  const summary =
    selected.length === 1 ? ALL_METRIC_LABEL[selected[0]] : selected.length === order.length ? "All metrics" : `${selected.length} metrics`;
  return (
    <div className="rd-filter-group mm-metrics-filter" ref={ref}>
      <FilterLabel label="Metrics" />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn mm-metrics-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel mm-metrics-panel">
          <div className="mm-metrics-quick">
            <button onClick={() => onChange(order)}>Select all</button>
            <button onClick={() => onChange(DEFAULT_SELECTION)}>Reset</button>
          </div>
          {groups.map((g) => {
            const onCount = g.keys.filter((k) => selected.includes(k)).length;
            const allOn = onCount === g.keys.length;
            return (
              <div key={g.id} className="mm-metrics-section">
                <label className={`mm-metrics-group mm-metrics-group-${g.id}`}>
                  <input type="checkbox" checked={allOn} onChange={() => setGroup(g.keys, !allOn)} />
                  <span>{g.label}</span>
                  <span className="mm-metrics-count">{onCount}/{g.keys.length}</span>
                </label>
                {g.keys.map((k) => (
                  <label key={k} className={`rd-option mm-metrics-option${selected.includes(k) ? " rd-selected" : ""}`}>
                    <input type="checkbox" checked={selected.includes(k)} onChange={() => toggle(k)} />
                    <span>{ALL_METRIC_LABEL[k]}</span>
                  </label>
                ))}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
function DateRangeFilter({ dates, from, to, onFrom, onTo, onClear, onToday }) {
  const toOptions = dates.filter((d) => !from || d >= from);
  return (
    <div className="rd-filter-group rd-date-range-group">
      <FilterLabel label="Date range" onClear={onClear} />
      <div className="rd-date-inputs">
        <select value={from} onChange={(e) => onFrom(e.target.value)} className="rd-dropdown-btn">
          {dates.map((d) => <option key={d} value={d}>{shortDate(d)}</option>)}
        </select>
        <span className="rd-date-sep">to</span>
        <select value={to} onChange={(e) => onTo(e.target.value)} className="rd-dropdown-btn">
          {toOptions.map((d) => <option key={d} value={d}>{shortDate(d)}</option>)}
        </select>
        <button onClick={onToday} className="occ-btn-today" title="Jump to today"><RotateCcw size={12} />TODAY</button>
      </div>
    </div>
  );
}
function ChipList({ items }) {
  const [open, setOpen] = useState(false);
  if (!items || items.length === 0) return <span className="mm-muted">—</span>;
  const LIMIT = 4;
  const shown = open ? items : items.slice(0, LIMIT);
  const rest = items.length - LIMIT;
  return (
    <div className="mm-chips-wrap">
      {shown.map((i, idx) => (
        <span key={idx} className={`mm-chip ${i.tone || "mm-chip-slate"}`} title={i.title || i.text}>
          <span className="mm-chip-text">{i.text}</span>
          {i.value && <span className="mm-chip-value">{i.value}</span>}
        </span>
      ))}
      {rest > 0 && (
        <button type="button" className="mm-chip-more" onClick={() => setOpen((o) => !o)}>
          {open ? "Show less" : `+${rest} more`}
        </button>
      )}
    </div>
  );
}
// One extranet value cell, styled by the field's format.
function ValueCell({ field, row, rowBg }) {
  const raw = row ? row[field.id] : null;
  const text = row ? fieldValues(row, field).join(", ") : "";
  const cls = `mm-ext-td ${rowBg}`;
  if (!text) return <td className={cls}><span className="mm-muted">—</span></td>;
  if (field.format === "badge") return <td className={cls}><span className={`mm-badge ${badgeTone(text)}`}>{text}</span></td>;
  if (field.format === "score") {
    const n = toNumber(raw);
    return <td className={cls}>{n === null ? text : <span className={`mm-score ${scoreTone(n)}`}>{text}</span>}</td>;
  }
  if (field.pending) {
    const n = toNumber(raw);
    return <td className={cls}>{n === null ? text : <span className={`mm-pending ${n > 0 ? "mm-pending-some" : "mm-pending-zero"}`}>{text}</span>}</td>;
  }
  // numbers (and number-only text such as a lookback of "30") are centred; words are left-aligned
  if (["percent", "int", "number", "datetime"].includes(field.format) || toNumber(text) !== null) return <td className={`${cls} mm-ext-num`}>{text}</td>;
  return <td className={`${cls} mm-ext-text`}>{text}</td>;
}

// One line per campaign / rule (Sponsored, VDI). Every column of the metric lists the same rows
// in the same order with a fixed line height, so line N is the same campaign / rule everywhere.
function StackCell({ field, rows, rowBg, titleOf }) {
  if (!rows.length) return <td className={`mm-ext-td ${rowBg}`}><span className="mm-muted">—</span></td>;
  const numeric = ["currency", "decimal2", "percent", "int", "number"].includes(field.format);
  return (
    <td className={`mm-ext-td mm-stack-td ${rowBg}`}>
      <div className="mm-stack">
        {rows.map((r, i) => {
          const text = fieldValues(r, field).join(", ");
          const title = [titleOf ? titleOf(r) : "", text].filter(Boolean).join(" · ");
          return (
            <div key={i} className={`mm-stack-line${numeric ? " mm-stack-num" : field.format === "badge" || field.format === "month" ? " mm-stack-center" : ""}`} title={title}>
              {!text ? <span className="mm-muted">—</span>
                : field.format === "badge" ? <span className={`mm-badge ${badgeTone(text)}`}>{text}</span>
                : text}
            </div>
          );
        })}
      </div>
    </td>
  );
}

// ================================================================= property detail pages
// "Click a hotel" detail pages, copied UNCHANGED from the old MMT Dashboard and Booking Dashboard
// (Dashboard.jsx) so MMT Metrics and Booking Metrics show exactly the same pages:
//   MMTPropertyDetailView      — old MMT Dashboard detail (used by this dashboard)
//   BookingOverviewDetailView  — old Booking Dashboard · Stay data detail (used by Booking Metrics)
//   VisitConversionDetailView  — old Booking Dashboard · Book data detail (used by Booking Metrics)
// They read the same API rows the old pages read (/api/mmt/summary, /api/booking/overview,
// /api/booking/visit-conversion). Only change: the back button text points at the new dashboards.
// They use the old pages' CSS classes in Dashboard.css — keep those rules when the old
// dashboards' code is deleted from Dashboard.jsx.

function formatDateLabel(iso) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short" });
}
function propertyMatchesFilters(meta, filters, excludeKey) {
  const checks = [
    ["name", filters.properties],
    ["location", filters.locations],
    ["agent", filters.agents],
  ];
  return checks.every(([key, selectedArr]) => {
    if (key === excludeKey) return true;
    if (!selectedArr || selectedArr.length === 0) return true;
    return selectedArr.includes(meta[key]);
  });
}
function getAvailableOptions(field, filters, metaList) {
  const filtered = metaList.filter((m) => propertyMatchesFilters(m, filters, field));
  return Array.from(new Set(filtered.map((m) => m[field]))).sort();
}
function matchesSearch(meta, query) {
  if (!query.trim()) return true;
  const q = query.trim().toLowerCase();
  return (
    (meta.name || "").toLowerCase().includes(q) ||
    (meta.location || "").toLowerCase().includes(q) ||
    (meta.agent || "").toLowerCase().includes(q)
  );
}
function DetailFilterLabel({ label, onClear }) {
  return (
    <div className="rd-filter-label-row">
      <label className="rd-filter-label">{label}</label>
      <button onClick={onClear} title={`Clear ${label}`} className="rd-clear-btn">
        <Eraser size={12} />
      </button>
    </div>
  );
}
function MultiSelectFilter({ label, options, selected, onChange, onClear }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  const selectAllRef = useRef(null);

  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  function toggleOption(opt) {
    if (selected.includes(opt)) {
      onChange(selected.filter((s) => s !== opt));
    } else {
      onChange([...selected, opt]);
    }
  }

  const filteredOptions = useMemo(() => {
    if (!query.trim()) return options;
    const q = query.trim().toLowerCase();
    return options.filter((o) => String(o).toLowerCase().includes(q));
  }, [options, query]);

  const allFilteredSelected =
    filteredOptions.length > 0 && filteredOptions.every((o) => selected.includes(o));
  const someFilteredSelected = filteredOptions.some((o) => selected.includes(o));

  useEffect(() => {
    if (selectAllRef.current) {
      selectAllRef.current.indeterminate = someFilteredSelected && !allFilteredSelected;
    }
  }, [someFilteredSelected, allFilteredSelected]);

  function toggleSelectAll() {
    if (allFilteredSelected) {
      onChange(selected.filter((s) => !filteredOptions.includes(s)));
    } else {
      const merged = new Set([...selected, ...filteredOptions]);
      onChange(Array.from(merged));
    }
  }

  const summary =
    selected.length === 0
      ? "All"
      : selected.length === 1
      ? selected[0]
      : `${selected.length} selected`;

  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <DetailFilterLabel label={label} onClear={onClear} />
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{summary}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <div className="rd-dropdown-search-wrap">
            <Search size={13} className="rd-dropdown-search-icon" />
            <input
              type="text"
              autoFocus
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder={`Search ${label.toLowerCase()}...`}
              className="rd-dropdown-search-input"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="rd-dropdown-search-clear"
                title="Clear search"
              >
                <X size={12} />
              </button>
            )}
          </div>

          {filteredOptions.length > 0 && (
            <label className="rd-option rd-option-selectall">
              <input
                type="checkbox"
                ref={selectAllRef}
                checked={allFilteredSelected}
                onChange={toggleSelectAll}
              />
              <span>Select All{query.trim() ? ` (${filteredOptions.length} shown)` : ""}</span>
            </label>
          )}

          {filteredOptions.length === 0 ? (
            <div className="rd-dropdown-empty">No matches</div>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = selected.includes(opt);
              return (
                <label
                  key={opt}
                  className={`rd-option${isSelected ? " rd-selected" : ""}`}
                >
                  <input
                    type="checkbox"
                    checked={isSelected}
                    onChange={() => toggleOption(opt)}
                  />
                  <span>{opt}</span>
                </label>
              );
            })
          )}
        </div>
      )}
    </div>
  );
}
function SearchBox({ value, onChange, onClear }) {
  return (
    <div className="rd-filter-group rd-search-group">
      <DetailFilterLabel label="Search" onClear={onClear} />
      <div className="rd-search-input-wrap">
        <Search size={14} className="rd-search-icon" />
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          placeholder="Property, location, agent..."
          className="rd-search-input"
        />
        {value && (
          <button onClick={onClear} className="rd-search-clear-btn" title="Clear search">
            <X size={13} />
          </button>
        )}
      </div>
    </div>
  );
}
function AvailableDateRangeFilter({ availableDates, fromDate, toDate, onFromChange, onToChange, onClear, onToday }) {
  const toDateOptions = availableDates.filter((d) => d >= fromDate);
  return (
    <div className="rd-filter-group rd-date-range-group">
      <DetailFilterLabel label="Date range" onClear={onClear} />
      <div className="rd-date-inputs">
        <select value={fromDate} onChange={(e) => onFromChange(e.target.value)} className="rd-dropdown-btn">
          {availableDates.map((d) => (
            <option key={d} value={d}>
              {formatDateLabel(d)}
            </option>
          ))}
        </select>
        <span className="rd-date-sep">to</span>
        <select value={toDate} onChange={(e) => onToChange(e.target.value)} className="rd-dropdown-btn">
          {toDateOptions.map((d) => (
            <option key={d} value={d}>
              {formatDateLabel(d)}
            </option>
          ))}
        </select>
        <button onClick={onToday} className="occ-btn-today" title="Jump to today">
          <RotateCcw size={12} />
          TODAY
        </button>
      </div>
    </div>
  );
}
function PropertySearchSelect({ label, value, options, onChange }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const wrapperRef = useRef(null);
  useEffect(() => {
    function handleClickOutside(e) {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) {
        setOpen(false);
        setQuery("");
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);
  const filteredOptions = options.filter((o) =>
    o.toLowerCase().includes(query.trim().toLowerCase())
  );
  function selectOption(opt) {
    onChange(opt);
    setOpen(false);
    setQuery("");
  }
  return (
    <div className="rd-filter-group" ref={wrapperRef}>
      <div className="rd-filter-label-row">
        <label className="rd-filter-label">{label}</label>
      </div>
      <button onClick={() => setOpen((o) => !o)} className="rd-dropdown-btn">
        <span className="rd-dropdown-summary">{value || "Select property"}</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="rd-dropdown-panel">
          <input
            type="text"
            autoFocus
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search property..."
            className="mmt-property-search-input"
          />
          {filteredOptions.length === 0 ? (
            <div className="mmt-property-search-empty">No matches</div>
          ) : (
            filteredOptions.map((opt) => (
              <button
                key={opt}
                onClick={() => selectOption(opt)}
                className={`rd-option${opt === value ? " rd-selected" : ""}`}
              >
                {opt}
              </button>
            ))
          )}
        </div>
      )}
    </div>
  );
}
function computeGrowthBadge(rawRows, hotelName, metric, fromDate, toDate) {
  if (!fromDate || !toDate) return null;
  const from = new Date(fromDate);
  const to = new Date(toDate);
  const rangeDays = Math.round((to - from) / 86400000) + 1;
  const priorTo = new Date(from);
  priorTo.setDate(priorTo.getDate() - 1);
  const priorFrom = new Date(priorTo);
  priorFrom.setDate(priorFrom.getDate() - (rangeDays - 1));
  const priorFromStr = priorFrom.toISOString().slice(0, 10);
  const priorToStr = priorTo.toISOString().slice(0, 10);
  function periodValue(m, f, t) {
    const vals = rawRows
      .filter((r) => r.hotel_name === hotelName && r.metric === m && r.date >= f && r.date <= t)
      .map((r) => Number(r.value) || 0);
    if (m === "ASP" || m === "Conversion") {
      const nonZero = vals.filter((v) => v > 0);
      if (nonZero.length === 0) return 0;
      return nonZero.reduce((s, v) => s + v, 0) / nonZero.length;
    }
    return vals.reduce((s, v) => s + v, 0);
  }
  const currentTotal = periodValue(metric, fromDate, toDate);
  const priorTotal = periodValue(metric, priorFromStr, priorToStr);
  if (priorTotal === 0) return null;
  const pct = ((currentTotal - priorTotal) / priorTotal) * 100;
  return { pct, up: pct >= 0 };
}
function MMTBarChart({ data, revxLabel = "REVX VALUE", compLabel = "COMP VALUE", showComp = true }) {
  const maxVal = Math.max(1, ...data.map((d) => (showComp ? Math.round(d.revx) + Math.round(d.comp) : Math.round(d.revx))));

  return (
    <div className="mmt-chart-card">
      <div className="mmt-chart-legend">
        <span className="mmt-legend-item"><span className="mmt-legend-dot mmt-dot-revx" />{revxLabel}</span>
        {showComp && <span className="mmt-legend-item"><span className="mmt-legend-dot mmt-dot-comp" />{compLabel}</span>}
      </div>
      <div className="mmt-chart-bars">
        {data.map((d) => {
          const revx = Math.round(d.revx);
          const comp = Math.round(d.comp);
          const total = showComp ? revx + comp : revx;
          const totalHeightPct = total > 0 ? (total / maxVal) * 100 : 2;
          const revxSharePct = total > 0 ? (revx / total) * 100 : 0;
          const compSharePct = total > 0 ? (comp / total) * 100 : 0;
          const isEmpty = total === 0;
          return (
            <div key={d.label} className="mmt-chart-group">
              <div
                className={`mmt-bar-stack${isEmpty ? " mmt-bar-stack-empty" : ""}`}
                style={{ height: `${totalHeightPct}%` }}
                title={
                  showComp
                    ? `${revxLabel}: ${revx.toLocaleString("en-IN")} · ${compLabel}: ${comp.toLocaleString("en-IN")}`
                    : `${revxLabel}: ${revx.toLocaleString("en-IN")}`
                }
              >
              {(() => {
                const barPixelHeight = (totalHeightPct / 100) * 200;
                const revxPixelHeight = barPixelHeight * (revxSharePct / 100);
                const compPixelHeight = barPixelHeight * (compSharePct / 100);
                const MIN_LABEL_HEIGHT = 14;
                return (
                  <>
                    {revx > 0 && (
                      <div className="mmt-bar-seg mmt-bar-seg-revx" style={{ height: `${revxSharePct}%` }}>
                        {revxPixelHeight >= MIN_LABEL_HEIGHT && (
                          <span className="mmt-bar-seg-value">{revx.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    )}
                    {showComp && comp > 0 && (
                      <div className="mmt-bar-seg mmt-bar-seg-comp" style={{ height: `${compSharePct}%` }}>
                        {compPixelHeight >= MIN_LABEL_HEIGHT && (
                          <span className="mmt-bar-seg-value">{comp.toLocaleString("en-IN")}</span>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
              </div>
              <div className="mmt-bar-label">{d.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}
const BOOKING_OVERVIEW_METRICS = [
  { key: "room_nights", abbr: "RN", revxField: "room_nights_revx", marketField: "room_nights_market", format: "int", aggregation: "sum" },
  { key: "adr", abbr: "ADR", revxField: "adr_revx", marketField: "adr_market", format: "currency", aggregation: "avg" },
  { key: "revenue", abbr: "REV", revxField: "revenue_revx", marketField: "revenue_market", format: "currency", aggregation: "sum" },
];
function formatBookingMetricValue(value, format) {
  const num = Number(value) || 0;
  if (format === "currency") return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${num.toFixed(2)}%`;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
const MMT_METRICS = [
  { key: "Room Nights", label: "Room Nights", abbr: "RN" },
  { key: "Revenue", label: "Revenue", abbr: "REV" },
  { key: "ASP", label: "ASP", abbr: "ASP" },
  { key: "Property Visits", label: "Property Visits", abbr: "VISITS" },
  { key: "Conversion", label: "Conversion", abbr: "CONV%" },
];
const MMT_METRIC_FORMAT = {
  "Room Nights": "int",
  Revenue: "currency",
  ASP: "currency",
  "Property Visits": "int",
  Conversion: "percent",
};
function formatMmtMetricValue(value, metric) {
  const format = MMT_METRIC_FORMAT[metric] || "int";
  const num = Number(value) || 0;
  if (format === "currency") return `₹${num.toLocaleString("en-IN", { maximumFractionDigits: 0 })}`;
  if (format === "percent") return `${num.toFixed(2)}%`;
  return num.toLocaleString("en-IN", { maximumFractionDigits: 0 });
}
export function MMTPropertyDetailView({ allRows, initialPropertyName, onBack, backLabel = "← Back to MMT Metrics" }) {
  const [activeMetric, setActiveMetric] = useState("Room Nights");
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPropertyName, setCurrentPropertyName] = useState(initialPropertyName);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false);
  const allDates = useMemo(
    () => Array.from(new Set(allRows.map((r) => r.date).filter(Boolean))).sort(),
    [allRows]
  );
  const [fromDate, setFromDate] = useState(allDates[0] || "");
  const [toDate, setToDate] = useState(allDates[allDates.length - 1] || "");
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }, [allDates]);
  function resetDateRange() {
    if (allDates.length > 0) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }
  function handleFromDateChange(newFrom) {
    setFromDate(newFrom);
    if (toDate && toDate < newFrom) setToDate(newFrom);
  }
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback =
      pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => {
      if (!map.has(r.hotel_name)) {
        map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent });
      }
    });
    return Array.from(map.values());
  }, [allRows]);
  const filters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locAgentFilters = { properties: [currentPropertyName], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(
    () => getAvailableOptions("location", locAgentFilters, propertyMeta),
    [propertyMeta, currentPropertyName, selectedAgents]
  );
  const agentOptions = useMemo(
    () => getAvailableOptions("agent", locAgentFilters, propertyMeta),
    [propertyMeta, currentPropertyName, selectedLocations]
  );
  const filteredPropertyOptions = useMemo(() => {
    return propertyMeta
      .filter((m) => propertyMatchesFilters(m, filters, null) && matchesSearch(m, searchQuery))
      .map((m) => m.name)
      .sort();
  }, [propertyMeta, selectedLocations, selectedAgents, searchQuery]);
  useEffect(() => {
    if (filteredPropertyOptions.length > 0 && !filteredPropertyOptions.includes(currentPropertyName)) {
      setCurrentPropertyName(filteredPropertyOptions[0]);
    }
  }, [filteredPropertyOptions]);
  const currentMeta =
    propertyMeta.find((m) => m.name === currentPropertyName) ||
    propertyMeta.find(
      (m) => m.name?.trim().toLowerCase() === (currentPropertyName || "").trim().toLowerCase()
    );
  function switchProperty(name) {
    setCurrentPropertyName(name);
    setSearchQuery("");
  }
  function clearAllFilters() {
    setSelectedLocations([]);
    setSelectedAgents([]);
    setSearchQuery("");
    resetDateRange();
  }
  const metricTotals = useMemo(() => {
    function sumMetric(key) {
      return allRows
        .filter((r) => r.hotel_name === currentPropertyName && r.metric === key && r.date >= fromDate && r.date <= toDate)
        .reduce((s, r) => s + (Number(r.value) || 0), 0);
    }
    function avgOfNonZeroDays(key) {
      const vals = allRows
        .filter((r) => r.hotel_name === currentPropertyName && r.metric === key && r.date >= fromDate && r.date <= toDate)
        .map((r) => Number(r.value) || 0)
        .filter((v) => v > 0);
      if (vals.length === 0) return 0;
      return vals.reduce((s, v) => s + v, 0) / vals.length;
    }
    return {
      "Room Nights": sumMetric("Room Nights"),
      Revenue: sumMetric("Revenue"),
      "Property Visits": sumMetric("Property Visits"),
      ASP: avgOfNonZeroDays("ASP"),
      Conversion: avgOfNonZeroDays("Conversion"),
    };
  }, [allRows, currentPropertyName, fromDate, toDate]);
  const chartData = useMemo(() => {
    const byDate = new Map();
    allRows.forEach((r) => {
      if (r.hotel_name !== currentPropertyName) return;
      if (r.metric !== activeMetric) return;
      if (fromDate && r.date < fromDate) return;
      if (toDate && r.date > toDate) return;
      byDate.set(r.date, { revx: Number(r.value) || 0, comp: Number(r.competitor_value) || 0 });
    });
    return Array.from(byDate.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([date, vals]) => ({
        label: new Date(date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        revx: Math.round(vals.revx),
        comp: Math.round(vals.comp),
      }));
  }, [allRows, currentPropertyName, activeMetric, fromDate, toDate]);
  const growth = computeGrowthBadge(allRows, currentPropertyName, activeMetric, fromDate, toDate);
  if (!currentMeta) {
    return (
      <div className="booking-subpage">
        <div className="booking-subpage-header">
          <button onClick={onBack} className="rd-btn-clear booking-back-btn">{backLabel}</button>
        </div>
        <div className="rd-blank-tab">No properties match the current filters.</div>
      </div>
    );
  }
  return (
    <div
      className="booking-subpage"
      style={{ display: "block", height: "100%", overflow: "auto", boxSizing: "border-box" }}
    >
      <div className="booking-subpage-header">
        <button onClick={onBack} className="rd-btn-clear booking-back-btn">
          {backLabel}
        </button>
        <h2 className="booking-subpage-title">{currentMeta.name}</h2>
        <span style={{ fontSize: 13, color: "#64748b" }}>
          {currentMeta.location} · Agent: {currentMeta.agent}
        </span>
      </div>
      <div className="rd-filter-bar occ-filter-bar">
        <button
          type="button"
          className="occ-mobile-filter-toggle"
          onClick={() => setMobileFiltersOpen((o) => !o)}
        >
          <span className="occ-mobile-filter-toggle-label">
            <Search size={14} aria-hidden="true" />
            Filters
          </span>
          <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
          <div className="rd-filter-row">
            <PropertySearchSelect
              label="Property Name"
              value={currentPropertyName}
              options={filteredPropertyOptions}
              onChange={switchProperty}
            />
            <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
            <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
            <SearchBox value={searchQuery} onChange={setSearchQuery} onClear={() => setSearchQuery("")} />
            <AvailableDateRangeFilter
              availableDates={allDates}
              fromDate={fromDate}
              toDate={toDate}
              onFromChange={handleFromDateChange}
              onToChange={setToDate}
              onClear={resetDateRange}
              onToday={goToToday}
            />
            <div className="rd-actions">
              <button onClick={clearAllFilters} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
      </div>
      <div
        className="booking-subpage-body"
        style={{ display: "block", padding: "20px 24px", overflow: "visible", width: "100%", boxSizing: "border-box" }}
      >
        <div className="mmt-detail-kpi-grid">
          {MMT_METRICS.map((m) => {
            const isActive = activeMetric === m.key;
            return (
              <button
                key={m.key}
                onClick={() => setActiveMetric(m.key)}
                className={`mmt-detail-card${isActive ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}
              >
                <div className="mmt-detail-card-label">{m.label}</div>
                <div className="mmt-detail-card-value">{formatMmtMetricValue(metricTotals[m.key], m.key)}</div>
              </button>
            );
          })}
        </div>
        <div className="mmt-detail-chart-card">
          <div className="mmt-detail-title-row">
            <div className="mmt-detail-title">{currentMeta.name}'s {activeMetric}</div>
            {growth && (
              <span className={`mmt-growth-badge ${growth.up ? "mmt-growth-up" : "mmt-growth-down"}`}>
                {growth.up ? "↑" : "↓"} {Math.abs(growth.pct).toFixed(0)}%
              </span>
            )}
          </div>
          <div className="mmt-detail-subtext">
            ({formatDateLabel(fromDate)} – {formatDateLabel(toDate)})
            {growth && " · vs. same length period immediately before"}
          </div>
          <div className="mmt-detail-total">
            {formatMmtMetricValue(metricTotals[activeMetric], activeMetric)}
          </div>
          {chartData.length === 0 ? (
            <div style={{ padding: 24, textAlign: "center", color: "#999" }}>No data for this metric/date range.</div>
          ) : (
            <MMTBarChart data={chartData} />
          )}
        </div>
      </div>
    </div>
  );
}
export function BookingOverviewDetailView({ allRows, initialPropertyName, onBack, backLabel = "← Back to Booking Metrics" }) {
  const [activeMetricKey, setActiveMetricKey] = useState("revenue");
  const [currentPropertyName, setCurrentPropertyName] = useState(initialPropertyName);
  const allDates = useMemo(() => Array.from(new Set(allRows.map((r) => r.record_date).filter(Boolean))).sort(), [allRows]);
  const [fromDate, setFromDate] = useState(allDates[0] || "");
  const [toDate, setToDate] = useState(allDates[allDates.length - 1] || "");
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }, [allDates]);
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => { if (!map.has(r.hotel_name)) map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent }); });
    return Array.from(map.values());
  }, [allRows]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const currentMeta = propertyMeta.find((m) => m.name === currentPropertyName);
  const switchFilters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(() => getAvailableOptions("location", switchFilters, propertyMeta), [propertyMeta, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", switchFilters, propertyMeta), [propertyMeta, selectedLocations]);
  const propertyOptions = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, switchFilters, null)).map((m) => m.name).sort(),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  useEffect(() => {
    if (propertyOptions.length > 0 && !propertyOptions.includes(currentPropertyName)) {
      setCurrentPropertyName(propertyOptions[0]);
    }
  }, [propertyOptions]);
  const rangeRows = useMemo(
    () => allRows.filter((r) => r.hotel_name === currentPropertyName && r.record_date >= fromDate && r.record_date <= toDate),
    [allRows, currentPropertyName, fromDate, toDate]
  );
  const totals = useMemo(() => {
    const out = {};
    BOOKING_OVERVIEW_METRICS.forEach((m) => {
      const sums = rangeRows.reduce((acc, r) => ({
        revx: acc.revx + (Number(r[m.revxField]) || 0),
        market: acc.market + (Number(r[m.marketField]) || 0),
        count: acc.count + 1,
      }), { revx: 0, market: 0, count: 0 });
      if (m.aggregation === "avg" && sums.count > 0) {
        out[m.key] = { revx: sums.revx / sums.count, market: sums.market / sums.count };
      } else {
        out[m.key] = { revx: sums.revx, market: sums.market };
      }
    });
    return out;
  }, [rangeRows]);
  const activeMetric = BOOKING_OVERVIEW_METRICS.find((m) => m.key === activeMetricKey);
  const chartData = useMemo(() => {
    return [...rangeRows]
      .sort((a, b) => a.record_date.localeCompare(b.record_date))
      .map((r) => ({
        label: new Date(r.record_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
        revx: Number(r[activeMetric.revxField]) || 0,
        comp: Number(r[activeMetric.marketField]) || 0,
      }));
  }, [rangeRows, activeMetric]);
  if (!currentMeta) {
    return (
      <div>
        <button onClick={onBack} className="rd-btn-clear booking-back-btn">{backLabel}</button>
        <div className="rd-blank-tab">No data for this property.</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "20px 24px", overflow: "auto", height: "100%", boxSizing: "border-box" }}>
      <button onClick={onBack} className="rd-btn-clear booking-back-btn" style={{ marginBottom: 12 }}>{backLabel}</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 className="booking-subpage-title" style={{ margin: 0 }}>{currentMeta.name}</h2>
        <span style={{ fontSize: 13, color: "#64748b" }}>{currentMeta.location} · Agent: {currentMeta.agent}</span>
      </div>
      <div className="rd-filter-row" style={{ marginBottom: 16 }}>
        <PropertySearchSelect label="Switch Property" value={currentPropertyName} options={propertyOptions} onChange={setCurrentPropertyName} />
        <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
        <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
        <AvailableDateRangeFilter
          availableDates={allDates} fromDate={fromDate} toDate={toDate}
          onFromChange={(v) => { setFromDate(v); if (toDate && toDate < v) setToDate(v); }}
          onToChange={setToDate}
          onClear={() => { setFromDate(allDates[0] || ""); setToDate(allDates[allDates.length - 1] || ""); }}
          onToday={goToToday}
        />
      </div>
      <div className="mmt-detail-kpi-grid booking-kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 210px))" }}>
        {BOOKING_OVERVIEW_METRICS.map((m) => {
          const isActive = activeMetricKey === m.key;
          return (
            <button key={m.key} onClick={() => setActiveMetricKey(m.key)} className={`mmt-detail-card${isActive ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
              <div className="mmt-detail-card-label">{m.abbr} — REVX</div>
              <div className="mmt-detail-card-value">{formatBookingMetricValue(totals[m.key]?.revx, m.format)}</div>
            </button>
          );
        })}
      </div>
      <div className="mmt-detail-chart-card">
        <div className="mmt-detail-title">{currentMeta.name}'s {activeMetric.abbr} — REVX vs Market</div>
        <div className="mmt-detail-subtext">{formatDateLabel(fromDate)} – {formatDateLabel(toDate)}</div>
        {chartData.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#999" }}>No data for this date range.</div>
        ) : (
          <div className="booking-chart-scroll">
            <MMTBarChart data={chartData} revxLabel="REVX" compLabel="MARKET" />
          </div>
        )}
      </div>
      <div className="mmt-asp-table-card" style={{ marginTop: 20 }}>
        <div className="mmt-asp-table-scroll">
          <table className="rd-table occ-table">
            <colgroup>
              <col style={{ width: 200 }} />
              {rangeRows.map((_, i) => <col key={i} style={{ width: 100 }} />)}
            </colgroup>
            <thead>
              <tr>
                <th className="rd-th-fixed" style={{ left: 0, width: 200 }}>METRIC</th>
                {rangeRows.map((r) => (
                  <th key={r.record_date} className="occ-th-metric booking-th-single">{formatDateLabel(r.record_date)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {BOOKING_OVERVIEW_METRICS.map((m) => (
                <React.Fragment key={m.key}>
                  <tr className="rd-matrix-row rd-row-even">
                    <td className="rd-td-fixed" style={{ left: 0, width: 200, backgroundColor: "#fff" }}>{m.abbr} (REVX)</td>
                    {rangeRows.map((r) => <td key={r.record_date} className="occ-td-adr">{formatBookingMetricValue(r[m.revxField], m.format)}</td>)}
                  </tr>
                  <tr className="rd-matrix-row rd-row-odd">
                    <td className="rd-td-fixed" style={{ left: 0, width: 200, backgroundColor: "#f8fafc" }}>{m.abbr} (Market)</td>
                    {rangeRows.map((r) => <td key={r.record_date} className="occ-td-a">{formatBookingMetricValue(r[m.marketField], m.format)}</td>)}
                  </tr>
                  <tr className="rd-matrix-row occ-subrow">
                    <td className="occ-subrow-label rd-td-fixed" style={{ left: 0, width: 200 }}>% Difference</td>
                    {rangeRows.map((r) => {
                      const revx = Number(r[m.revxField]) || 0;
                      const mkt = Number(r[m.marketField]) || 0;
                      const pct = mkt !== 0 ? ((revx - mkt) / mkt) * 100 : 0;
                      return <td key={r.record_date} className="occ-td-t" style={{ color: pct >= 0 ? "#166534" : "#7f1d1d" }}>{pct >= 0 ? "+" : ""}{pct.toFixed(1)}%</td>;
                    })}
                  </tr>
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
export function VisitConversionDetailView({ allRows, initialPropertyName, onBack, backLabel = "← Back to Booking Metrics" }) {
  const [currentPropertyName, setCurrentPropertyName] = useState(initialPropertyName);
  const [activeMetricKey, setActiveMetricKey] = useState("bookings");
  const allDates = useMemo(() => Array.from(new Set(allRows.map((r) => r.record_date).filter(Boolean))).sort(), [allRows]);
  const [fromDate, setFromDate] = useState(allDates[0] || "");
  const [toDate, setToDate] = useState(allDates[allDates.length - 1] || "");
  useEffect(() => {
    if (allDates.length > 0 && !fromDate && !toDate) {
      setFromDate(allDates[0]);
      setToDate(allDates[allDates.length - 1]);
    }
  }, [allDates]);
  function goToToday() {
    if (allDates.length === 0) return;
    const realToday = new Date().toISOString().slice(0, 10);
    const exact = allDates.includes(realToday) ? realToday : null;
    const pastOrToday = allDates.filter((d) => d <= realToday);
    const fallback = pastOrToday.length > 0 ? pastOrToday[pastOrToday.length - 1] : allDates[allDates.length - 1];
    const target = exact || fallback;
    setFromDate(target);
    setToDate(target);
  }
  const propertyMeta = useMemo(() => {
    const map = new Map();
    allRows.forEach((r) => { if (!map.has(r.hotel_name)) map.set(r.hotel_name, { name: r.hotel_name, location: r.location, agent: r.agent }); });
    return Array.from(map.values());
  }, [allRows]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedAgents, setSelectedAgents] = useState([]);
  const currentMeta = propertyMeta.find((m) => m.name === currentPropertyName);
  const switchFilters = { properties: [], locations: selectedLocations, agents: selectedAgents };
  const locationOptions = useMemo(() => getAvailableOptions("location", switchFilters, propertyMeta), [propertyMeta, selectedAgents]);
  const agentOptions = useMemo(() => getAvailableOptions("agent", switchFilters, propertyMeta), [propertyMeta, selectedLocations]);
  const propertyOptions = useMemo(
    () => propertyMeta.filter((m) => propertyMatchesFilters(m, switchFilters, null)).map((m) => m.name).sort(),
    [propertyMeta, selectedLocations, selectedAgents]
  );
  useEffect(() => {
    if (propertyOptions.length > 0 && !propertyOptions.includes(currentPropertyName)) {
      setCurrentPropertyName(propertyOptions[0]);
    }
  }, [propertyOptions]);
  const rangeRows = useMemo(
    () => [...allRows.filter((r) => r.hotel_name === currentPropertyName && r.record_date >= fromDate && r.record_date <= toDate)].sort((a, b) => a.record_date.localeCompare(b.record_date)),
    [allRows, currentPropertyName, fromDate, toDate]
  );
  const totals = useMemo(() => {
    const views = rangeRows.reduce((s, r) => s + (Number(r.page_views) || 0), 0);
    const bookings = rangeRows.reduce((s, r) => s + (Number(r.daily_bookings) || 0), 0);
    const conv = views > 0 ? (bookings / views) * 100 : 0;
    const avgViews = rangeRows.length > 0 ? views / rangeRows.length : 0;
    return { views, bookings, conv, avgViews };
  }, [rangeRows]);
  const chartData = useMemo(() => {
    return rangeRows.map((r) => ({
      label: new Date(r.record_date).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }),
      revx:
        activeMetricKey === "bookings"
          ? Number(r.daily_bookings) || 0
          : activeMetricKey === "views"
          ? Number(r.page_views) || 0
          : Number(r.conversion_pct) || 0,
      comp: 0,
    }));
  }, [rangeRows, activeMetricKey]);
  if (!currentMeta) {
    return (
      <div>
        <button onClick={onBack} className="rd-btn-clear booking-back-btn">{backLabel}</button>
        <div className="rd-blank-tab">No data for this property.</div>
      </div>
    );
  }
  return (
    <div style={{ padding: "20px 24px", overflow: "auto", height: "100%", boxSizing: "border-box" }}>
      <button onClick={onBack} className="rd-btn-clear booking-back-btn" style={{ marginBottom: 12 }}>{backLabel}</button>
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <h2 className="booking-subpage-title" style={{ margin: 0 }}>{currentMeta.name}</h2>
        <span style={{ fontSize: 13, color: "#64748b" }}>{currentMeta.location} · Agent: {currentMeta.agent}</span>
      </div>
      <div className="rd-filter-row" style={{ marginBottom: 16 }}>
        <PropertySearchSelect label="Switch Property" value={currentPropertyName} options={propertyOptions} onChange={setCurrentPropertyName} />
        <MultiSelectFilter label="Location" options={locationOptions} selected={selectedLocations} onChange={setSelectedLocations} onClear={() => setSelectedLocations([])} />
        <MultiSelectFilter label="Agent" options={agentOptions} selected={selectedAgents} onChange={setSelectedAgents} onClear={() => setSelectedAgents([])} />
        <AvailableDateRangeFilter
          availableDates={allDates} fromDate={fromDate} toDate={toDate}
          onFromChange={(v) => { setFromDate(v); if (toDate && toDate < v) setToDate(v); }}
          onToChange={setToDate}
          onClear={() => { setFromDate(allDates[0] || ""); setToDate(allDates[allDates.length - 1] || ""); }}
          onToday={goToToday}
        />
      </div>
      <div className="booking-funnel-card">
        <div className="booking-funnel-row">
          <div className="booking-funnel-step">
            <div className="booking-funnel-step-value">{totals.views.toLocaleString("en-IN")}</div>
            <div className="booking-funnel-step-label">Property Page Views</div>
          </div>
          <div className="booking-funnel-arrow">
            →
            <span>{totals.conv.toFixed(2)}% converted</span>
          </div>
          <div className="booking-funnel-step">
            <div className="booking-funnel-step-value">{totals.bookings.toLocaleString("en-IN")}</div>
            <div className="booking-funnel-step-label">Bookings</div>
          </div>
        </div>
      </div>
      <div className="mmt-detail-kpi-grid booking-kpi-grid" style={{ gridTemplateColumns: "repeat(auto-fit, minmax(150px, 210px))" }}>
        <button onClick={() => setActiveMetricKey("bookings")} className={`mmt-detail-card${activeMetricKey === "bookings" ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
          <div className="mmt-detail-card-label">Daily Bookings</div>
          <div className="mmt-detail-card-value">{totals.bookings.toLocaleString("en-IN")}</div>
        </button>
        <button onClick={() => setActiveMetricKey("views")} className={`mmt-detail-card${activeMetricKey === "views" ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
          <div className="mmt-detail-card-label">Property Page Views</div>
          <div className="mmt-detail-card-value">{totals.views.toLocaleString("en-IN")}</div>
          <span className="rd-rating-reviews">Avg: {totals.avgViews.toLocaleString("en-IN", { maximumFractionDigits: 1 })}/day</span>
        </button>
        <button onClick={() => setActiveMetricKey("conversion")} className={`mmt-detail-card${activeMetricKey === "conversion" ? " mmt-detail-card-active" : " mmt-card-tint-green"}`}>
          <div className="mmt-detail-card-label">Conversion</div>
          <div className="mmt-detail-card-value">{totals.conv.toFixed(2)}%</div>
        </button>
      </div>
      <div className="mmt-detail-chart-card">
        <div className="mmt-detail-title">
          {currentMeta.name}'s {activeMetricKey === "bookings" ? "Bookings" : activeMetricKey === "views" ? "Page Views" : "Conversion"} trend
        </div>
        <div className="mmt-detail-subtext">{formatDateLabel(fromDate)} – {formatDateLabel(toDate)}</div>
        {chartData.length === 0 ? (
          <div style={{ padding: 24, textAlign: "center", color: "#999" }}>No data for this date range.</div>
        ) : (
          <div className="booking-chart-scroll">
            <MMTBarChart
              data={chartData}
              showComp={false}
              revxLabel={activeMetricKey === "bookings" ? "BOOKINGS" : activeMetricKey === "views" ? "PAGE VIEWS" : "CONV %"}
            />
          </div>
        )}
      </div>
    </div>
  );
}

// The old MMT detail chart has no horizontal scroll of its own: with a long date range (100+ days)
// its bars are wider than the card, so the WHOLE page slid sideways under the menu. Same fix the
// Booking detail charts already have (.booking-chart-scroll): the chart scrolls inside its own
// card. Only the MMT chart sits directly inside .mmt-detail-chart-card, so Booking is unaffected.
const MMT_DETAIL_CHART_CSS = `
.mmt-detail-chart-card > .mmt-chart-card { overflow-x: auto; }
`;

// "Last N days" box (old Booking Dashboard's Visit & Conversion page) — used in both dashboards'
// filter rows. Type a number → the date range becomes the last N dates that have data.
export function LastNDaysInput({ dates, onApply, label = "Last N days" }) {
  const [value, setValue] = useState("");
  function apply(v) {
    setValue(v);
    const n = parseInt(v, 10);
    if (!Number.isFinite(n) || n <= 0 || !dates || dates.length === 0) return;
    onApply(dates[Math.max(0, dates.length - n)], dates[dates.length - 1]);
  }
  return (
    <div className="rd-filter-group" style={{ width: 110 }}>
      <div className="rd-filter-label-row">
        <label className="rd-filter-label">{label}</label>
        {value && (
          <button type="button" onClick={() => setValue("")} title={`Clear ${label}`} className="rd-clear-btn">
            <Eraser size={12} />
          </button>
        )}
      </div>
      <input
        type="number"
        min="1"
        inputMode="numeric"
        value={value}
        onChange={(e) => apply(e.target.value)}
        placeholder="e.g. 15"
        title="Show the last N days that have data"
        style={{
          width: "100%", boxSizing: "border-box", padding: "7px 10px", border: "1px solid #e2e8f0",
          borderRadius: 6, fontSize: 13, background: "#fff", fontFamily: "inherit",
        }}
      />
    </div>
  );
}

// ================================================================= page
export default function MmtMetricsDashboard() {
  const [selected, setSelected] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
      const valid = Array.isArray(stored) ? stored.filter((k) => ALL_METRIC_LABEL[k]) : [];
      return valid.length ? valid : DEFAULT_SELECTION;
    } catch {
      return DEFAULT_SELECTION;
    }
  });
  const [trendRows, setTrendRows] = useState(null);
  const [trendError, setTrendError] = useState(null);
  const [extranet, setExtranet] = useState(null);
  const [extranetError, setExtranetError] = useState(null);
  const [ratingRows, setRatingRows] = useState(null);

  const [hotelSel, setHotelSel] = useState([]);
  const [locationSel, setLocationSel] = useState([]);
  const [agentSel, setAgentSel] = useState([]);
  const [search, setSearch] = useState("");
  const [metricFilters, setMetricFilters] = useState({}); // "<metric>.<field>" -> selected values
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");
  const [showComp, setShowComp] = useState(false);
  const [showVar, setShowVar] = useState(false);
  const [sort, setSort] = useState({ key: "hotel", dir: "asc" });
  const [totalModes, setTotalModes] = useState(DEFAULT_TOTAL_MODES);
  const [refreshing, setRefreshing] = useState(false);
  const [syncing, setSyncing] = useState(false);
  const [toast, setToast] = useState(null);
  const pollRef = useRef(null);
  const [mobileFiltersOpen, setMobileFiltersOpen] = useState(false); // phones: filters start collapsed
  const [filterTab, setFilterTab] = useState(null); // which metric's filters the "Metric filters" panel shows
  const [detailKey, setDetailKey] = useState(null); // hotel whose detail page is open
  const theadRef = useRef(null);
  const waitRef = useRef(null);

  const perfSel = useMemo(() => selected.filter((k) => PERF_BY_KEY[k]).map((k) => PERF_BY_KEY[k]), [selected]);
  const extSel = useMemo(() => selected.filter((k) => EXT_BY_KEY[k]).map((k) => EXT_BY_KEY[k]), [selected]);
  const needsTrends = perfSel.length > 0;

  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(selected));
    } catch {
      // ignore
    }
  }, [selected]);

  // ---------------- loading
  async function loadExtranet(force = false) {
    try {
      const res = await fetch(apiUrl(`/api/mmt-metrics/data${force ? "?forceRefresh=true" : ""}`));
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load");
      setExtranet(json);
      setExtranetError(null);
      return true;
    } catch (err) {
      setExtranetError(err.message);
      setExtranet((prev) => prev || { metrics: {}, lastSync: {}, errors: {}, tables: {} });
      return false;
    }
  }
  async function loadTrends(force = false) {
    try {
      const res = await fetch(apiUrl(`/api/mmt/summary${force ? "?forceRefresh=true" : ""}`));
      const json = await res.json();
      if (!json.success) throw new Error(json.error || "Failed to load");
      setTrendRows(json.data || []);
      setTrendError(null);
      return true;
    } catch (err) {
      setTrendError(err.message);
      setTrendRows((prev) => prev || []);
      return false;
    }
  }
  async function loadRatings() {
    try {
      const json = await (await fetch(apiUrl("/api/rankly/summary"))).json();
      setRatingRows(json.success ? json.data || [] : []);
    } catch {
      setRatingRows([]);
    }
  }
  useEffect(() => {
    loadExtranet(false); // also carries every last-sync time (performance included)
    loadRatings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (needsTrends && trendRows === null) loadTrends(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [needsTrends]);
  useEffect(() => {
    if (!toast) return undefined;
    const t = setTimeout(() => setToast(null), 7000);
    return () => clearTimeout(t);
  }, [toast]);
  function stopPolling() {
    if (pollRef.current) clearInterval(pollRef.current);
    if (waitRef.current) clearTimeout(waitRef.current);
    pollRef.current = null;
    waitRef.current = null;
  }
  useEffect(() => () => stopPolling(), []);

  async function refresh() {
    if (refreshing) return;
    setRefreshing(true);
    const results = await Promise.all([loadExtranet(true), trendRows !== null || needsTrends ? loadTrends(true) : Promise.resolve(true)]);
    setRefreshing(false);
    setToast(results.every(Boolean)
      ? { type: "success", message: "Showing the latest data from Athena." }
      : { type: "error", message: "Couldn't refresh all data." });
  }
  async function startSync() {
    if (syncing) return;
    // old metrics -> revx-mmt-scraper, new metrics -> mmt-extranet-sync (only what is ticked)
    const target = needsTrends && extSel.length ? "all" : needsTrends ? "trends" : "extranet";
    setSyncing(true);
    try {
      const res = await fetch(apiUrl(`/api/mmt-metrics/sync?target=${target}`), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ target }),
      });
      const json = await res.json();
      if (!json.success) throw new Error(Object.values(json.errors || {}).join(" · ") || json.error || "Couldn't start sync.");
      const failed = Object.entries(json.errors || {}).map(([k, v]) => `${k}: ${v}`);
      setToast({ type: failed.length ? "error" : "success", message: failed.length ? `Sync partly started — ${failed.join(" · ")}` : json.message });
    } catch (err) {
      setSyncing(false);
      setToast({ type: "error", message: err.message });
      return;
    }
    stopPolling();
    pollRef.current = setInterval(() => {
      loadExtranet(true);
      if (trendRows !== null) loadTrends(true);
    }, SYNC_POLL_MS);
    waitRef.current = setTimeout(async () => {
      stopPolling();
      await Promise.all([loadExtranet(true), trendRows !== null ? loadTrends(true) : Promise.resolve(true)]);
      setSyncing(false);
      setToast({ type: "success", message: "Data has been refreshed after the sync." });
    }, SYNC_WAIT_MS);
  }

  // ---------------- hotel identity (master_id first, name as fallback)
  // VDI: show only the rules for the CURRENT and NEXT month (the table keeps one rule per month
  // going back years). A rule is kept when its month (rule_start_date) is this month or next month,
  // or its date range overlaps them. Rolls forward automatically each month. Display only — the
  // VDI task (Task page) reads the API itself and uses the current month's rule.
  const extRows = useMemo(() => {
    const metrics = extranet?.metrics || {};
    if (!metrics.vdi) return metrics;
    const now = new Date();
    const p2 = (n) => String(n).padStart(2, "0");
    const ym = (d) => `${d.getFullYear()}-${p2(d.getMonth() + 1)}`;
    const thisMonth = ym(now);
    const nextMonth = ym(new Date(now.getFullYear(), now.getMonth() + 1, 1));
    const windowStart = `${thisMonth}-01`;
    const windowEnd = `${nextMonth}-31`;
    const vdi = metrics.vdi.filter((r) => {
      const start = String(r.rule_start_date || "").slice(0, 10);
      const end = String(r.rule_end_date || "").slice(0, 10);
      const month = start.slice(0, 7);
      if (month === thisMonth || month === nextMonth) return true;
      return !!(start && end && start <= windowEnd && end >= windowStart);
    });
    return { ...metrics, vdi };
  }, [extranet]);
  const nameToKey = useMemo(() => {
    const map = {};
    const add = (r) => {
      if (r.master_id && r.hotel_name) map[norm(r.hotel_name)] = `M:${norm(r.master_id)}`;
    };
    (trendRows || []).forEach(add);
    Object.values(extRows).forEach((rows) => (rows || []).forEach(add));
    return map;
  }, [trendRows, extRows]);
  const keyOf = (r) => (r.master_id ? `M:${norm(r.master_id)}` : nameToKey[norm(r.hotel_name)] || `N:${norm(r.hotel_name)}`);

  // extranet rows grouped per metric -> hotel key
  const extByHotel = useMemo(() => {
    const out = {};
    Object.entries(extRows).forEach(([mk, rows]) => {
      const byHotel = {};
      (rows || []).forEach((r) => {
        if (!r.hotel_name && !r.master_id) return;
        const k = keyOf(r);
        (byHotel[k] = byHotel[k] || []).push(r);
      });
      out[mk] = byHotel;
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extRows, nameToKey]);

  // ---------------- performance: trends index + dates
  const trendIndex = useMemo(() => {
    const map = {}; // key -> date -> metric -> {value, comp}
    (trendRows || []).forEach((r) => {
      const k = keyOf(r);
      ((map[k] = map[k] || {})[r.date] = map[k][r.date] || {})[r.metric] = {
        value: Number(r.value) || 0,
        comp: Number(r.competitor_value) || 0,
      };
    });
    return map;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendRows, nameToKey]);
  const allDates = useMemo(
    () => (needsTrends ? Array.from(new Set((trendRows || []).map((r) => r.date).filter(Boolean))).sort() : []),
    [trendRows, needsTrends]
  );
  function defaultRange() {
    if (!allDates.length) return ["", ""];
    return [allDates[Math.max(0, allDates.length - 7)], allDates[allDates.length - 1]];
  }
  useEffect(() => {
    if (!allDates.length) return;
    if (!fromDate || !toDate || !allDates.includes(fromDate) || !allDates.includes(toDate)) {
      const [f, t] = defaultRange();
      setFromDate(f);
      setToDate(t);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allDates]);
  function goToToday() {
    const today = new Date().toISOString().slice(0, 10);
    const pick = allDates.includes(today) ? today : allDates.filter((d) => d <= today).pop() || allDates[allDates.length - 1];
    setFromDate(pick);
    setToDate(pick);
  }
  const dateColumns = useMemo(
    () => (needsTrends ? allDates.filter((d) => d >= fromDate && d <= toDate).slice(0, MAX_DATE_COLUMNS) : []),
    [allDates, fromDate, toDate, needsTrends]
  );

  // ---------------- hotels in the table (from every ticked source)
  const hotels = useMemo(() => {
    const map = new Map();
    const add = (r) => {
      if (!r.hotel_name && !r.master_id) return;
      const key = keyOf(r);
      if (!map.has(key)) map.set(key, { key, name: String(r.hotel_name || r.master_id).trim(), location: r.location || "", agent: r.agent || "" });
      const h = map.get(key);
      if (!h.location && r.location) h.location = r.location;
      if (!h.agent && r.agent) h.agent = r.agent;
    };
    if (needsTrends) (trendRows || []).forEach(add);
    extSel.forEach((m) => (extRows[m.key] || []).forEach(add));
    return Array.from(map.values());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trendRows, extRows, extSel, needsTrends, nameToKey]);

  // ---------------- filters
  // every column of every ticked extranet metric is a filter (MMT Dashboard Requirements)
  const metricFilterDefs = useMemo(
    () => extSel.flatMap((m) => m.fields.map((f) => ({ id: `${m.key}.${f.id}`, metric: m.key, field: f, label: f.label }))),
    [extSel]
  );
  const activeMf = metricFilterDefs.filter((d) => (metricFilters[d.id] || []).length);
  // a row passes when it matches every active filter of its own metric (except `exceptId`)
  function rowPasses(row, metricKey, exceptId) {
    return activeMf.every((d) => {
      if (d.metric !== metricKey || d.id === exceptId) return true;
      const sel = metricFilters[d.id];
      return fieldValues(row, d.field).some((v) => sel.includes(v));
    });
  }
  function hotelPasses(h, except) {
    if (except !== "hotel" && hotelSel.length && !hotelSel.includes(h.name)) return false;
    if (except !== "location" && locationSel.length && !locationSel.includes(h.location)) return false;
    if (except !== "agent" && agentSel.length && !agentSel.includes(h.agent)) return false;
    const s = search.trim().toLowerCase();
    if (s && ![h.name, h.location, h.agent].some((v) => String(v || "").toLowerCase().includes(s))) return false;
    for (const m of extSel) {
      if (!activeMf.some((d) => d.metric === m.key && d.id !== except)) continue;
      if (!(extByHotel[m.key]?.[h.key] || []).some((r) => rowPasses(r, m.key, except))) return false;
    }
    return true;
  }
  const optionsFor = (field) =>
    Array.from(new Set(hotels.filter((h) => hotelPasses(h, field)).map((h) => (field === "hotel" ? h.name : h[field])).filter(Boolean)))
      .sort((a, b) => a.localeCompare(b));
  function metricFilterOptions(d) {
    const values = new Set();
    hotels.filter((h) => hotelPasses(h, d.id)).forEach((h) => {
      (extByHotel[d.metric]?.[h.key] || []).forEach((r) => {
        if (rowPasses(r, d.metric, d.id)) fieldValues(r, d.field).forEach((v) => values.add(v));
      });
    });
    const list = Array.from(values);
    if (d.field.format === "month") {
      const t = (v) => {
        const x = Date.parse(`1 ${v}`);
        return Number.isNaN(x) ? 0 : x;
      };
      return list.sort((a, b) => t(b) - t(a) || a.localeCompare(b)); // newest month first
    }
    const numeric = list.length > 0 && list.every((v) => toNumber(v) !== null);
    return numeric ? list.sort((a, b) => toNumber(a) - toNumber(b)) : list.sort((a, b) => a.localeCompare(b));
  }

  // "Metric filters" panel: one tab per ticked extranet metric
  const mfMetrics = extSel.map((m) => {
    const defs = metricFilterDefs.filter((d) => d.metric === m.key);
    return { key: m.key, label: m.label, defs, active: defs.filter((d) => (metricFilters[d.id] || []).length).length };
  });
  const mfTab = mfMetrics.find((x) => x.key === filterTab) || mfMetrics[0] || null;
  const mfActiveTotal = mfMetrics.reduce((sum, x) => sum + x.active, 0);

  // ---------------- ratings (Rankly, MMT + Goibibo)
  const ratingLookup = useMemo(() => {
    const rows = ratingRows || [];
    const dates = Array.from(new Set(rows.map((r) => r.record_date).filter(Boolean))).sort();
    const latest = dates[dates.length - 1];
    const byName = {};
    rows.forEach((r) => {
      if (r.record_date !== latest || !r.location_name) return;
      const platform = String(r.platform || "").toLowerCase();
      if (platform !== "makemytrip" && platform !== "goibibo") return;
      if (r.avg_rating === null || r.avg_rating === "") return;
      (byName[r.location_name] = byName[r.location_name] || {})[platform] = { rating: Number(r.avg_rating), reviews: Number(r.total_reviews) || 0 };
    });
    const names = Object.keys(byName);
    const lower = Object.fromEntries(names.map((n) => [n.trim().toLowerCase(), n]));
    const cache = new Map();
    function resolve(hotelName) {
      if (cache.has(hotelName)) return cache.get(hotelName);
      let found = byName[hotelName] ? hotelName : lower[String(hotelName).trim().toLowerCase()] || null;
      if (!found) {
        let best = 0;
        names.forEach((n) => {
          const sc = nameScore(hotelName, n);
          if (sc > best) {
            best = sc;
            found = n;
          }
        });
        if (best < 0.6) found = null;
      }
      cache.set(hotelName, found);
      return found;
    }
    return (hotelName, platform) => {
      const n = resolve(hotelName);
      return n ? byName[n][platform] || null : null;
    };
  }, [ratingRows]);

  // ---------------- table rows
  const extCols = useMemo(() => extSel.flatMap((m) => m.cols.map((c) => ({ ...c, metric: m.key }))), [extSel]);
  function cellValue(h, col) {
    const rows = (extByHotel[col.metric]?.[h.key] || []).filter((r) => rowPasses(r, col.metric, null));
    if (col.kind === "promotions") {
      return rows
        .slice()
        .sort((a, b) => (toNumber(b.max_discount) || 0) - (toNumber(a.max_discount) || 0))
        .map((r) => {
          const disc = formatValue(r.max_discount, "percent");
          const rn = formatValue(r.room_nights, "int");
          const tier = formatValue(r.tier, "badge");
          const status = formatValue(r.status, "badge");
          return {
            text: `${r.promotion_name || "Promotion"}${tier ? ` · ${tier}` : ""}`,
            value: [disc, rn ? `${rn} RN` : ""].filter(Boolean).join(" · "),
            tone: /expired|inactive|deactiv/i.test(status) ? "mm-chip-slate" : tierTone(r.tier),
            title: `${r.promotion_name || "Promotion"}\nTier: ${tier || "—"}\nMax discount: ${disc || "—"}\nRoom nights: ${rn || "—"}\nStatus: ${status || "—"}`,
          };
        });
    }
    if (col.kind === "inclusions") {
      return rows.map((r) => {
        const status = formatValue(r.status, "badge");
        const live = !status || /^active|^live/i.test(status);
        return {
          text: r.description || "",
          tone: live ? "mm-chip-green" : "mm-chip-amber",
          title: `${r.description || ""}\nStatus: ${status || "—"}${r.rooms ? `\nRooms: ${r.rooms}` : ""}`,
        };
      });
    }
    if (col.kind === "stack") {
      // one line per campaign / rule, same order in every column of this metric
      const def = EXT_BY_KEY[col.metric];
      return { stack: rows.slice().sort(def.stackSort || (() => 0)) };
    }
    if (col.kind === "list") {
      const f = fieldOf(col.metric, col.field);
      return Array.from(new Set(rows.flatMap((r) => fieldValues(r, f)))).map((t) => ({ text: t }));
    }
    return rows[0] || null; // "value": the hotel's row, rendered by ValueCell
  }
  const tableRows = useMemo(() => {
    return hotels.filter((h) => hotelPasses(h, null)).map((h) => {
      const ext = {};
      extCols.forEach((c) => {
        ext[c.id] = cellValue(h, c);
      });
      const perDate = {}; // metric -> [{value, comp, varv}] per date
      const totals = {}; // metric -> {value, comp, varv}
      perfSel.forEach((m) => {
        const cells = dateColumns.map((d) => {
          const c = trendIndex[h.key]?.[d]?.[m.trend];
          const value = c?.value || 0;
          const comp = c?.comp || 0;
          return { value, comp, varv: value - comp };
        });
        perDate[m.key] = cells;
        // same as the MMT Dashboard: Sum, Average (sum / number of dates), or for ASP REV/RN
        const sum = (field) => cells.reduce((s, c) => s + c[field], 0);
        const mode = totalModes[m.key] || DEFAULT_TOTAL_MODES[m.key];
        let tv;
        let tc;
        if (m.key === "asp" && mode === "revrn") {
          const ratio = (field) => {
            const rn = dateColumns.reduce((s, d) => s + (trendIndex[h.key]?.[d]?.["Room Nights"]?.[field] || 0), 0);
            const rev = dateColumns.reduce((s, d) => s + (trendIndex[h.key]?.[d]?.Revenue?.[field] || 0), 0);
            return rn > 0 ? rev / rn : 0;
          };
          tv = ratio("value");
          tc = ratio("comp");
        } else if (mode === "average") {
          const n = dateColumns.length || 1;
          tv = sum("value") / n;
          tc = sum("comp") / n;
        } else {
          tv = sum("value");
          tc = sum("comp");
        }
        totals[m.key] = { value: tv, comp: tc, varv: tv - tc };
      });
      return { ...h, ext, perDate, totals };
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hotels, hotelSel, locationSel, agentSel, search, metricFilters, extCols, perfSel, dateColumns, trendIndex, extByHotel, totalModes]);

  const sortedRows = useMemo(() => {
    const arr = [...tableRows];
    const dir = sort.dir === "desc" ? -1 : 1;
    const valueOf = (r) => {
      if (sort.key === "hotel") return r.name;
      if (sort.key === "rating:makemytrip" || sort.key === "rating:goibibo") return ratingLookup(r.name, sort.key.slice(7))?.rating ?? null;
      if (sort.key.startsWith("ext:")) {
        const id = sort.key.slice(4);
        const v = r.ext[id];
        if (Array.isArray(v)) return v.length;
        const col = extCols.find((c) => c.id === id);
        if (v && v.stack) {
          const top = v.stack[0]?.[col?.field];
          return toNumber(top) ?? (top ? String(top) : null);
        }
        const raw = v && col?.field ? v[col.field] : null;
        return toNumber(raw) ?? (raw ? String(raw) : null);
      }
      let m = sort.key.match(/^d:(\d+):([a-z_]+):(value|comp|varv)$/);
      if (m) return r.perDate[m[2]]?.[Number(m[1])]?.[m[3]] ?? null;
      m = sort.key.match(/^t:([a-z_]+):(value|comp|varv)$/);
      if (m) return r.totals[m[1]]?.[m[2]] ?? null;
      return null;
    };
    arr.sort((a, b) => compareValues(valueOf(a), valueOf(b), dir));
    return arr;
  }, [tableRows, sort, ratingLookup, extCols]);

  function headerClick(key) {
    setSort((s) => (s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: key === "hotel" ? "asc" : "desc" }));
  }
  const mark = (key) => (sort.key === key ? <span className="rd-sort-indicator">{sort.dir === "asc" ? "▲" : "▼"}</span> : null);
  function clearAll() {
    setHotelSel([]);
    setLocationSel([]);
    setAgentSel([]);
    setSearch("");
    setMetricFilters({});
    const [f, t] = defaultRange();
    setFromDate(f);
    setToDate(t);
  }

  // ---------------- sticky header offsets = real heights of header rows 1 and 2
  useLayoutEffect(() => {
    const thead = theadRef.current;
    if (!thead) return undefined;
    const update = () => {
      const rows = thead.rows;
      if (!rows || rows.length < 3) return;
      const h1 = rows[0].getBoundingClientRect().height;
      const h2 = rows[1].getBoundingClientRect().height;
      thead.style.setProperty("--mm-top2", `${h1}px`);
      thead.style.setProperty("--mm-top3", `${h1 + h2}px`);
    };
    update();
    if (typeof ResizeObserver === "undefined") return undefined;
    const ro = new ResizeObserver(update);
    ro.observe(thead);
    return () => ro.disconnect();
  });

  // ---------------- header / status
  const kinds = ["value", ...(showComp ? ["comp"] : []), ...(showVar ? ["varv"] : [])];
  const perDateCols = perfSel.length * kinds.length;
  const hasDates = dateColumns.length > 0 && perfSel.length > 0;
  const loading = extranet === null || (needsTrends && trendRows === null);
  // one pill per data source: Performance (revx-mmt-scraper) and Extranet (mmt-extranet-sync,
  // which writes all extranet tables in the same run; click it for each metric's own time)
  const extSyncDetails = extSel.map((m) => [m.label, extranet?.lastSync?.[m.key]]);
  const errors = [
    trendError && needsTrends ? `Performance: ${trendError}` : null,
    extranetError ? `Extranet: ${extranetError}` : null,
    ...extSel.filter((m) => extranet?.errors?.[m.key]).map((m) => `${m.label}: ${extranet.errors[m.key]}`),
  ].filter(Boolean);
  const totalCols = 3 + extCols.length + (hasDates ? (dateColumns.length + 1) * perDateCols : 0);
  const kindLabel = (k) => (k === "comp" ? "COMP" : k === "varv" ? "VAR" : "REVX");

  // ---------------- "click a hotel" detail page (replaces the old MMT Dashboard's detail page)
  function openDetail(key) {
    if (trendRows === null) loadTrends(false); // performance data is needed for the detail page
    setDetailKey(key);
  }
  if (detailKey) {
    if (trendRows === null) return <div className="rd-blank-tab">Loading MMT data...</div>;
    // the old page is keyed by the hotel name exactly as /api/mmt/summary returns it
    const row = trendRows.find((r) => keyOf(r) === detailKey);
    const name = row ? row.hotel_name : hotels.find((h) => h.key === detailKey)?.name;
    return (
      <>
        <style>{MMT_DETAIL_CHART_CSS}</style>
        <MMTPropertyDetailView
          allRows={trendRows}
          initialPropertyName={name}
          onBack={() => setDetailKey(null)}
        />
      </>
    );
  }

  return (
    <div className="mm-page">
      <style>{MM_CSS}</style>
      {toast && (
        <div className={`rd-sync-toast rd-sync-toast-${toast.type}`}>
          <span>{toast.message}</span>
          <button onClick={() => setToast(null)} className="rd-sync-toast-close" aria-label="Dismiss"><X size={14} /></button>
        </div>
      )}
      <div className="mm-header">
        <div className="mm-title">MMT METRICS</div>
        <div className="mm-sync-line">
          {needsTrends && <SyncPill label="Performance" value={extranet?.lastSync?.trends} />}
          {extSel.length > 0 && (
            <SyncPill
              label="Extranet"
              details={extSyncDetails}
              note="All extranet metrics are fetched by the same daily sync (mmt-extranet-sync). The pill shows the oldest one."
            />
          )}
        </div>
      </div>

      <div className="rd-filter-bar occ-filter-bar mm-filter-bar">
        <button type="button" className="occ-mobile-filter-toggle" onClick={() => setMobileFiltersOpen((o) => !o)}>
          <span className="occ-mobile-filter-toggle-label"><Search size={14} aria-hidden="true" />Filters</span>
          <span className="occ-mobile-filter-mini-badges">
            <span>{selected.length} metric{selected.length === 1 ? "" : "s"}</span>
            <ChevronDown size={16} className={`occ-expand-icon ${mobileFiltersOpen ? "occ-expanded" : ""}`} />
          </span>
        </button>
        <div className={`occ-filter-body${mobileFiltersOpen ? "" : " occ-filter-body-collapsed"}`}>
        <div className="rd-filter-row mm-filter-row">
          <MetricsFilter selected={selected} onChange={setSelected} />
          <MultiSelect label="Hotel Name" options={optionsFor("hotel")} selected={hotelSel} onChange={setHotelSel} />
          <MultiSelect label="Location" options={optionsFor("location")} selected={locationSel} onChange={setLocationSel} />
          <MultiSelect label="Agent" options={optionsFor("agent")} selected={agentSel} onChange={setAgentSel} />
          <div className="rd-filter-group rd-search-group">
            <FilterLabel label="Search" onClear={search ? () => setSearch("") : null} />
            <div className="rd-search-input-wrap">
              <Search size={14} className="rd-search-icon" />
              <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Property, location, agent..." className="rd-search-input" />
            </div>
          </div>
          {/* date range + toggles + buttons wrap together as one unit */}
          <div className="mm-row-tail">
            {needsTrends && allDates.length > 0 && (
              <DateRangeFilter
                dates={allDates}
                from={fromDate}
                to={toDate}
                onFrom={(v) => {
                  setFromDate(v);
                  if (toDate && toDate < v) setToDate(v);
                }}
                onTo={setToDate}
                onClear={() => {
                  const [f, t] = defaultRange();
                  setFromDate(f);
                  setToDate(t);
                }}
                onToday={goToToday}
              />
            )}
            {needsTrends && allDates.length > 0 && (
              <LastNDaysInput
                dates={allDates}
                onApply={(f, t) => {
                  setFromDate(f);
                  setToDate(t);
                }}
              />
            )}
            {needsTrends && (
              <>
                <button type="button" onClick={() => setShowComp((v) => !v)} className={`rd-btn-toggle${showComp ? " rd-btn-toggle-active" : ""}`} title={showComp ? "Hide competitor data" : "Show competitor data"}>
                  COMP
                </button>
                <button type="button" onClick={() => setShowVar((v) => !v)} className={`rd-btn-toggle${showVar ? " rd-btn-toggle-active" : ""}`} title={showVar ? "Hide variance vs comp" : "Show variance vs comp (REVX minus COMP)"}>
                  VAR
                </button>
              </>
            )}
            <div className="rd-actions">
              {canUseSync() && (
                <>
                  <button onClick={refresh} disabled={refreshing} className={`rd-btn-sync${refreshing ? " rd-btn-sync-active" : ""}`} title="Show the latest data currently in Athena">
                    <RefreshCw size={14} className={refreshing ? "rd-spin" : ""} />
                    {refreshing ? "Refreshing..." : "Refresh"}
                  </button>
                  <button onClick={startSync} disabled={syncing} className={`rd-btn-sync${syncing ? " rd-btn-sync-active" : ""}`} title="Fetch new data from MMT now for the ticked metrics (10–15 minutes)">
                    <CloudDownload size={14} className={syncing ? "rd-spin" : ""} />
                    {syncing ? "Syncing..." : "Sync"}
                  </button>
                </>
              )}
              <button onClick={clearAll} className="rd-btn-clear"><X size={14} />Clear all filters</button>
            </div>
          </div>
        </div>
        {mfTab && (
          <div className="mm-mf-panel">
            <div className="mm-mf-tabs" role="tablist" aria-label="Metric filters">
              <span className="mm-mf-heading">Metric filters</span>
              {mfMetrics.map((x) => (
                <button
                  key={x.key}
                  type="button"
                  role="tab"
                  aria-selected={x.key === mfTab.key}
                  className={`mm-mf-tab${x.key === mfTab.key ? " mm-mf-tab-active" : ""}`}
                  onClick={() => setFilterTab(x.key)}
                  title={x.active ? `${x.active} filter${x.active === 1 ? "" : "s"} in use` : undefined}
                >
                  {x.label}
                  {x.active > 0 && <span className="mm-mf-badge">{x.active}</span>}
                </button>
              ))}
              {mfActiveTotal > 0 && (
                <button type="button" className="mm-mf-clear" onClick={() => setMetricFilters({})}>
                  Clear metric filters
                </button>
              )}
            </div>
            <div className="mm-mf-row" role="tabpanel">
              {mfTab.defs.map((d) => (
                <MultiSelect
                  key={d.id}
                  label={d.label}
                  options={metricFilterOptions(d)}
                  selected={metricFilters[d.id] || []}
                  onChange={(v) => setMetricFilters((prev) => ({ ...prev, [d.id]: v }))}
                />
              ))}
            </div>
          </div>
        )}
        </div>
      </div>

      {errors.length > 0 && <div className="mm-error">{errors.join(" · ")}</div>}
      {needsTrends && dateColumns.length === MAX_DATE_COLUMNS && allDates.filter((d) => d >= fromDate && d <= toDate).length > MAX_DATE_COLUMNS && (
        <div className="mm-note">Showing the first {MAX_DATE_COLUMNS} dates of the selected range.</div>
      )}

      {loading ? (
        <div className="rd-blank-tab">Loading MMT metrics...</div>
      ) : (
        <div className="rd-matrix-section mm-matrix-section">
          <div className="rd-matrix-card">
            <div className="rd-matrix-scroll">
              <table className="rd-table occ-table mmt-table mm-dyn-sticky">
                <colgroup>
                  <col className="mm-col-hotel" style={{ width: HOTEL_W }} />
                  <col style={{ width: SIDE_W }} />
                  <col style={{ width: SIDE_W }} />
                  {extCols.map((c) => <col key={`col-${c.id}`} style={{ width: extColWidth(c) }} />)}
                  {hasDates && dateColumns.map((d) =>
                    [...Array(perDateCols)].map((_, i) => <col key={`${d}-${i}-col`} style={{ width: METRIC_W }} />)
                  )}
                  {hasDates && perfSel.flatMap((m) => kinds.map((k) => <col key={`total-${m.key}-${k}-col`} style={{ width: TOTAL_W }} />))}
                </colgroup>
                <thead ref={theadRef}>
                  <tr>
                    <th rowSpan={3} onClick={() => headerClick("hotel")} style={{ left: 0, width: HOTEL_W }} className="rd-th-fixed">
                      <span className="rd-count-badge">{sortedRows.length}</span>
                      HOTEL NAME{mark("hotel")}
                    </th>
                    <th colSpan={2} rowSpan={2} style={{ left: HOTEL_W, width: SIDE_W * 2 }} className="rd-th-fixed rd-side">
                      RATING
                    </th>
                    {extSel.map((m) => (
                      <th key={m.key} colSpan={m.cols.length} className="occ-th-group">{m.label.toUpperCase()}</th>
                    ))}
                    {hasDates && dateColumns.map((d) => (
                      <th key={d} colSpan={perDateCols} className="occ-th-group">{shortDate(d)}</th>
                    ))}
                    {hasDates && perfSel.map((m, i) => (
                      <th key={`tb-${m.key}`} colSpan={kinds.length} className={`occ-th-group rd-total-header-blank${i === 0 ? " rd-total-edge" : ""}`} />
                    ))}
                  </tr>
                  <tr>
                    {extCols.map((c) => (
                      <th key={c.id} rowSpan={2} onClick={() => headerClick(`ext:${c.id}`)} className="mmt-th-metric-group mm-ext-head">
                        {c.label.toUpperCase()}{mark(`ext:${c.id}`)}
                      </th>
                    ))}
                    {hasDates && dateColumns.map((d) =>
                      perfSel.map((m) => (
                        <th key={`${d}-${m.key}`} colSpan={kinds.length} className="mmt-th-metric-group">{m.abbr}</th>
                      ))
                    )}
                    {hasDates && perfSel.map((m, i) => (
                      <th
                        key={`t-${m.key}`}
                        colSpan={kinds.length}
                        onClick={() => headerClick(`t:${m.key}:value`)}
                        className={`mmt-th-metric-group rd-total-grid-dark${i === 0 ? " rd-total-edge" : ""}`}
                        style={{ cursor: "pointer" }}
                      >
                        {m.abbr} TOTAL{mark(`t:${m.key}:value`)}
                      </th>
                    ))}
                  </tr>
                  <tr>
                    <th onClick={() => headerClick("rating:makemytrip")} className="mmt-th-subcol mmt-th-rating-sub" style={{ left: HOTEL_W, zIndex: 25 }}>
                      MMT{mark("rating:makemytrip")}
                    </th>
                    <th onClick={() => headerClick("rating:goibibo")} className="mmt-th-subcol mmt-th-rating-sub" style={{ left: HOTEL_W + SIDE_W, zIndex: 25 }}>
                      GOIBIBO{mark("rating:goibibo")}
                    </th>
                    {hasDates && dateColumns.map((d, i) =>
                      perfSel.map((m) =>
                        kinds.map((k) => (
                          <th key={`${d}-${m.key}-${k}`} onClick={() => headerClick(`d:${i}:${m.key}:${k}`)} className="mmt-th-subcol">
                            {kindLabel(k)}{mark(`d:${i}:${m.key}:${k}`)}
                          </th>
                        ))
                      )
                    )}
                    {hasDates && perfSel.map((m, i) => (
                      <React.Fragment key={`ts-${m.key}`}>
                        <th className={`mmt-th-subcol rd-total-grid-dark${i === 0 ? " rd-total-edge" : ""}`}>
                          <select
                            value={totalModes[m.key]}
                            onChange={(e) => setTotalModes((prev) => ({ ...prev, [m.key]: e.target.value }))}
                            onClick={(e) => e.stopPropagation()}
                            className="rd-total-select"
                          >
                            <option value="sum">Sum</option>
                            {m.key === "asp" ? <option value="revrn">REV/RN</option> : <option value="average">Average</option>}
                          </select>
                        </th>
                        {showComp && <th onClick={() => headerClick(`t:${m.key}:comp`)} className="mmt-th-subcol rd-total-grid-dark">COMP TOTAL{mark(`t:${m.key}:comp`)}</th>}
                        {showVar && <th onClick={() => headerClick(`t:${m.key}:varv`)} className="mmt-th-subcol rd-total-grid-dark">VAR TOTAL{mark(`t:${m.key}:varv`)}</th>}
                      </React.Fragment>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {sortedRows.length === 0 ? (
                    <tr>
                      <td colSpan={totalCols} className="mm-empty">No hotels match these filters.</td>
                    </tr>
                  ) : (
                    sortedRows.map((r, rIdx) => {
                      const rowBg = rIdx % 2 === 0 ? "rd-row-even" : "rd-row-odd";
                      return (
                        <tr key={r.key} className={rowBg}>
                          <td
                            style={{ left: 0, width: HOTEL_W }}
                            className={`rd-td-fixed ${rowBg} mm-hotel-link`}
                            title={`Click to open ${r.name}'s detail page${r.location ? ` · ${r.location}` : ""}${r.agent ? ` · ${r.agent}` : ""}`}
                            onClick={() => openDetail(r.key)}
                          >
                            {r.name}
                          </td>
                          {[["makemytrip", HOTEL_W], ["goibibo", HOTEL_W + SIDE_W]].map(([p, left]) => {
                            const e = ratingLookup(r.name, p);
                            return (
                              <td key={p} style={{ left, width: SIDE_W, backgroundColor: ratingColor(e ? e.rating : null) }} className="rd-td-fixed rd-side rd-rank-cell">
                                {e ? (
                                  <>
                                    <span className="rd-rating-value">{e.rating.toFixed(1)}</span>
                                    <span className="rd-rating-reviews">{e.reviews} reviews</span>
                                  </>
                                ) : (
                                  "—"
                                )}
                              </td>
                            );
                          })}
                          {extCols.map((c) =>
                            c.kind === "value"
                              ? <ValueCell key={c.id} field={fieldOf(c.metric, c.field)} row={r.ext[c.id]} rowBg={rowBg} />
                              : c.kind === "stack"
                                ? <StackCell key={c.id} field={fieldOf(c.metric, c.field)} rows={r.ext[c.id]?.stack || []} rowBg={rowBg} titleOf={EXT_BY_KEY[c.metric].stackTitle} />
                                : <td key={c.id} className={`mm-ext-td mm-list-td ${rowBg}`}><ChipList items={r.ext[c.id]} /></td>
                          )}
                          {hasDates && dateColumns.map((d, i) => (
                            <React.Fragment key={d}>
                              {perfSel.map((m) => {
                                const cell = r.perDate[m.key][i];
                                return (
                                  <React.Fragment key={m.key}>
                                    <td className="occ-td-t">{formatPerf(cell.value, m.format)}</td>
                                    {showComp && <td className="occ-td-a">{formatPerf(cell.comp, m.format)}</td>}
                                    {showVar && (
                                      <td className={cell.varv >= 0 ? "mmt-growth-up" : "mmt-growth-down"}>
                                        {cell.varv >= 0 ? "▲" : "▼"} {formatPerf(Math.abs(cell.varv), m.format)}
                                      </td>
                                    )}
                                  </React.Fragment>
                                );
                              })}
                            </React.Fragment>
                          ))}
                          {hasDates && perfSel.map((m, mIdx) => {
                            const t = r.totals[m.key];
                            return (
                              <React.Fragment key={`t-${m.key}`}>
                                <td className={`rd-total-td ${rowBg}${mIdx === 0 ? " rd-total-edge" : ""}`}>{formatPerf(t.value, m.format)}</td>
                                {showComp && <td className={`rd-total-td ${rowBg}`}>{formatPerf(t.comp, m.format)}</td>}
                                {showVar && (
                                  <td className={`rd-total-td ${rowBg} ${t.varv >= 0 ? "mmt-growth-up" : "mmt-growth-down"}`}>
                                    {t.varv >= 0 ? "▲" : "▼"} {formatPerf(Math.abs(t.varv), m.format)}
                                  </td>
                                )}
                              </React.Fragment>
                            );
                          })}
                        </tr>
                      );
                    })
                  )}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// nulls / empty values always sort last, numbers numerically, text alphabetically
function compareValues(va, vb, dir) {
  const empty = (v) => v === null || v === undefined || v === "";
  if (empty(va)) return empty(vb) ? 0 : 1;
  if (empty(vb)) return -1;
  const cmp = typeof va === "number" && typeof vb === "number" ? va - vb : String(va).localeCompare(String(vb));
  return cmp * dir;
}
